/* ══════════════════════════════════════════════════════════════════════════
 * A VENUE — the screen every card in Eleat opens onto
 *
 * ⚠️ AL, 18 SEPTEMBER 2026, ON WHY THIS COMES BEFORE HOME:
 * "if you dont have the restaurant about page with all the deals, signature
 *  menu, address reserve, heart, location pin, phone, facilites and ameneties,
 *  about, 4 deals, feeds and reveiws then what the fuck are you going to build
 *  on the homepage"
 * He is right. Home is a wall of cards and every one of them opens this. Build
 * Home first and every card leads nowhere.
 *
 * ⚠️ EVERY LABEL IS COPIED FROM `Eleater Prototype/11_Restaurant.html`. The
 * eight tabs, in its order, with its exact wording. RULE 4: DO NOT INVENT
 * ELEAT. If a word here is wrong it is wrong on that screen too, and both
 * change together.
 *
 * ⚠️ AND THE SINGULARS ARE NOT A TYPO. The venue page says "Daily Deal" and
 * "Last-Minute Special"; Home says "Daily Deals" and "Last-Minute Specials".
 * One venue has one of each. Home shows many. Both are locked wording.
 * ══════════════════════════════════════════════════════════════════════════ */

import { useEffect, useRef, useState } from 'react';
import { venue as api, getToken } from './api.js';
import { needsAccount, countVenueView } from './gate.jsx';
import { takePendingReview } from './pending.js';
import { DinedSheet } from './bookask.jsx';
import { track } from './track.js';
import { markSaved, useSaved } from './saved.js';
import { useLiked, markLiked } from './liked.js';
import { TabBar, Stars, priceText, HeartIcon, priceBand } from './ui.jsx';
import { openShare, shareVenue, shareFeedPost, SHARE_PATHS } from './share.js';
/* ⚠️ WHERE THE ELEATER IS STANDING — read, never re-derived. `place.js` owns
 * the centre and follows them as they walk; a second localStorage read on this
 * screen would be a second copy of one fact and would go stale the moment they
 * moved. `metresBetween` is its own measurement, used below because the
 * single-venue route sends no distance — see the note beside it. */
import { useCentre, useDevice, metresBetween } from './place.js';
/* ⚠️ EVERY ROUTING RULE LIVES IN ./directions.js AND NONE OF THEM IS DECIDED
 * HERE: the destination is the coordinate and never the name, the origin is
 * omitted rather than guessed when Eleat does not know, and the distance is
 * labelled as a distance and never as a travel time. Read that file's header. */
import { directionsHref, canRoute, defaultMode, howFar } from './directions.js';

import { TABS, tabsFor, openTab } from './tabs.js';
/* ⚠️⚠️ GAME DAY IS NOT A DEAL LIST AND NEVER WAS. `11_Restaurant.html` shows
 * the GAME first — sport, league, teams, kick-off, the day it is on — and the
 * menu is computed from that game's own start time. This pane had been built
 * here as a flat list of `deal_type='game_day'` rows with none of it, and the
 * tables that hold the real thing (`game_day_menus`, `game_day_games`,
 * `game_day_menu_items`, all from migration 019, August) had never been read
 * by one line of this repository. See the header of `GameDay.jsx`. */
import GameDayPane from './GameDay.jsx';
import { zh, label, dish, dealText, aboutText, captionOf, ownFacilities, uiLang } from './i18n.js';
/* ⚠️ THE TAB LABEL AND THE SCHEMA KEY ARE SEPARATE THINGS, ON PURPOSE.
 * `deal_type` is the enum in 001_schema.sql — happy_hour · daily · last_minute
 * · game_day. The label is what an Eleater reads. Collapsing them would mean a
 * copy change becoming a database migration. */
/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️⚠️ A VENUE SHOWS ONLY THE DEALS IT ACTUALLY HAS — 22 September 2026.
 *
 * Al: *"Not every restaurant will have all four deals… if they don't put
 * anything in, for example, Game Day, then Game Day should not show up as one
 * of their tabs. Signature Menu will always be there because it's not a deal…
 * whatever they choose or type in, that shows up. The other ones don't."*
 *
 * Until now BOTH this page and the locked screen it was built from drew all
 * eight tabs for every venue, so a Cantonese restaurant that has never shown
 * a football match in its life had a Game Day tab with nothing behind it.
 *
 * ── IT IS "HAS ONE", NOT "ON RIGHT NOW" ────────────────────────────────────
 * Wing Wednesday keeps its tab on a Sunday. A tab that appears and vanishes
 * through the week makes a venue's page change shape day to day, and somebody
 * planning Friday could not see Friday's Happy Hour. The tab is there; the
 * tab says when it runs.
 *
 * The distinction is free, as it turns out: isLiveNow() checks the deal's
 * overall run and its status, and does NOT check the weekday — the weekly
 * pattern lives in `happy_hour_schedules` and is display data. So the ordinary
 * public call already returns Wing Wednesday on a Sunday.
 *
 * ── AND NO COLUMN RECORDS THIS ─────────────────────────────────────────────
 * ⚠️ Deliberately. A `does_happy_hour` flag on the venue would be a second
 * copy of something the deals already say, and a second copy is the fault
 * behind /v1, the sixteen connector files and the two sign-in doors. A venue
 * has a Happy Hour if it has a published Happy Hour. Nothing to maintain,
 * nothing to drift.
 *
 * ── FEEDS AND REVIEWS ALWAYS SHOW ──────────────────────────────────────────
 * Al: *"I think we always show the Feeds and Reviews. This way it'll force the
 * restaurant also to put something up… at least we'll be populated
 * everywhere."* An empty Feeds tab is a nudge to the restaurant and an honest
 * answer to the Eleater. Deals are different: an empty Happy Hour tab is a
 * promise the venue never made.
 * ══════════════════════════════════════════════════════════════════════════ */
/* ⚠️ TABS, tabsFor() AND openTab() LIVE IN ./tabs.js. They were declared here
   until 22 September, which was fine while this was the only screen that
   needed them — Home, Explore, the View All pages and Zaryna all ask the same
   question, and four copies of one rule is the fault this repository keeps
   hitting. The long note explaining the rule is in that file. */

/* ⚠️ THE FOUR MENU SECTIONS AND THEIR ORDER ARE LOCKED, AND I HAD IT WRONG, AND I HAD IT WRONG — 18 September 2026.
 * 001_schema.sql:
 *     create type deal_section as enum
 *       ('alcoholic','food','combos','non_alcoholic'); -- LOCKED display order
 * The comment that stood here said alcoholic-first was a deliberate decision
 * that must not be undone — and the list underneath it started with food. The
 * warning and the violation were in the same file, in the same commit, written
 * in the same minute. Read the enum, not the note about the enum. */
const SECTIONS = [
  ['alcoholic', 'Alcoholic'],
  ['food', 'Food'],
  ['combos', 'Combos'],
  ['non_alcoholic', 'Non-alcoholic'],
];

/* ⚠️ HOURS ARE WINDOWS OF "HH:MM" STRINGS, NOT MINUTES — and I had this wrong
 * in the first version of this file. The database function that decides
 * "open now" (eleat_is_open_now) reads
 *     {"mon":[{"open":"17:00","close":"23:00"}], …}
 * and a screen that expects a different shape is a screen that disagrees with
 * the thing deciding the answer.
 *
 * ⚠️ A DAY CAN HAVE MORE THAN ONE WINDOW. Man Ho serves lunch, closes, then
 * serves dinner. Rendering only the first would tell somebody the place is
 * open at four o'clock when its doors are shut. */
/* 151 — the venue's own clock, set when its page draws (one venue per page) */
let VENUE_CLOCK = '12h';
function setVenueClock(f) { VENUE_CLOCK = f === '24h' ? '24h' : '12h'; }
function clock(hhmm) {
  const [h, m] = String(hhmm).split(':').map(Number);
  const hh = h % 24;
  if (VENUE_CLOCK === '24h') return `${String(hh).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  const ampm = hh < 12 ? 'AM' : 'PM';
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ "NaN:00 PM" ON EVERY DEAL — clock() WAS BEING FED A TIMESTAMP.
 *
 * `clock()` above is for OPENING HOURS, which arrive as `"17:00"`. A deal's
 * `start_time` and `end_time` are **`timestamptz`** (migration 001, lines 192
 * and 193) and arrive as `"2026-09-25T12:00:00.000Z"`.
 *
 *   String(ts).split(':')  →  ["2026-09-25T12", "00", "00.000Z"]
 *   Number("2026-09-25T12") →  NaN
 *   NaN % 24               →  NaN
 *
 * So every Happy Hour, Daily Deal, Last-Minute and Game Day window printed
 * **"NaN:00 AM"**, and nothing threw — a wrong string renders exactly as
 * happily as a right one.
 *
 * ⚠️ TWO INPUTS, TWO FUNCTIONS, DELIBERATELY. Teaching `clock()` to sniff
 * which kind of string it got is how one function ends up quietly wrong for
 * both. `"17:00"` is a time of day with no date and no zone; a timestamp is
 * an instant. They are not the same thing and they do not share a parser.
 *
 * ⚠️ AND IT IS SHOWN IN THE READER'S OWN ZONE, which for somebody standing
 * outside the restaurant IS the restaurant's zone. `GET /restaurants/:id`
 * does not send the venue's timezone, so inventing one here would be a
 * guess — this is the honest answer with what the route actually returns.
 * ══════════════════════════════════════════════════════════════════════════ */
function dealClock(ts, tz) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz || undefined,
      ...(VENUE_CLOCK === '24h' ? { hourCycle: 'h23' } : {}) });
  } catch {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit',
      ...(VENUE_CLOCK === '24h' ? { hourCycle: 'h23' } : {}) });
  }
}


/* ⚠️ "12:00 PM – 3:00 PM · 6:00 PM – 10:00 PM" — every window, separated, so a
 * gap in the middle of the day is visible rather than averaged away. */
function dayHours(windows) {
  if (!Array.isArray(windows) || !windows.length) return null;
  return windows.map((w) => `${clock(w.open)} – ${clock(w.close)}`).join(' · ');
}

const DAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'],
              ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️ THE TIME LINE OF A DEAL, PER KIND — 2 October 2026, found walking the app.
 * One line printed `start_time – end_time` for every kind, and the kinds do
 * not carry the same thing in those fields:
 *   · Happy Hour   → "15:00" / "20:00" off `happy_hour_schedules`; the
 *                    timestamp parser read them as nothing and drew "–".
 *   · Daily Deal   → the DATE RANGE it runs (today → 31 Dec); drawn as clock
 *                    times it read "0:00 – 23:59", which says nothing.
 *   · Last-Minute  → a window across days; drawn as times it read
 *                    "23:38 – 21:11", ending before it started.
 * The locked `11_Restaurant.html` answers each: Happy Hour's `#hhTime`
 * "Today · 5:00–7:00 PM"; Daily's `#ddChip` names a DAY, no clock; Last-
 * Minute's `when` "Until 10:00 PM". All in the venue's own zone.
 * ══════════════════════════════════════════════════════════════════════════ */
function dealWhen(d, kind, dayIdx, tz, runsToday = true) {
  if (kind === 'hh') {
    const sched = Array.isArray(d.schedule) ? d.schedule : [];
    const today = DAYS.findIndex(([k]) => k === todayKey(tz));
    const idx = dayIdx == null ? today : dayIdx;
    const row = sched.find((r) => r.day_of_week === idx);
    const open = row ? row.open_time : d.start_time;
    const close = row ? row.close_time : d.end_time;
    if (!/^\d{1,2}:\d{2}/.test(String(open || '')) || !/^\d{1,2}:\d{2}/.test(String(close || ''))) return null;
    const lbl = idx === today ? 'Today' : DAYS[idx][1];
    return `${lbl} · ${clock(String(open).slice(0, 5))} – ${clock(String(close).slice(0, 5))}`;
  }
  /* 125 — a Daily Deal's serving time, when the venue set one; none = all day */
  if (kind === 'daily') {
    const win = d.serve_window;
    if (!win || !/^\d{1,2}:\d{2}/.test(String(win.from || '')) || !/^\d{1,2}:\d{2}/.test(String(win.to || ''))) return null;
    const today = DAYS.findIndex(([k]) => k === todayKey(tz));
    const idx = dayIdx == null ? today : dayIdx;
    const times = `${clock(win.from)} – ${clock(win.to)}`;
    // All week, and the deal is not on today: the times alone — "Today" on a
    // deal that is not on today sends somebody to the door for nothing.
    if (dayIdx == null && !runsToday) return times;
    const lbl = idx === today ? 'Today' : DAYS[idx][1];
    return `${lbl} · ${times}`;
  }
  if (kind === 'lm') {
    if (!d.end_time) return null;
    const end = new Date(d.end_time);
    if (Number.isNaN(end.getTime())) return null;
    let sameDay = true;
    try {
      const f = new Intl.DateTimeFormat('en-CA', { timeZone: tz || undefined });
      sameDay = f.format(end) === f.format(new Date());
    } catch { /* the phone's day, as before */ }
    const day = sameDay ? '' : `${new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: tz || undefined }).format(end)} `;
    return `Until ${day}${dealClock(d.end_time, tz)}`;
  }
  return null;
}

/* ⚠️ "TODAY" IS TODAY WHERE THE RESTAURANT IS, NOT WHERE THE PHONE IS.
 * CLAUDE.md: times are stored UTC and shown in the venue's own IANA zone, and
 * `restaurant_timezone_is_iana` refuses an offset. An Eleater in Calgary
 * looking at a Hong Kong venue at 9pm Friday is looking at Saturday lunchtime
 * there — reading the phone's own day would tell them the place is shut when
 * it is serving. The zone is on the venue record; it is used, not ignored.
 *
 * ⚠️ AND IT FALLS BACK RATHER THAN THROWS. A bad or missing zone makes
 * Intl.DateTimeFormat throw, and a venue page that white-screens because one
 * field is wrong is worse than one showing the wrong day. */
/* the next seven days' dates, indexed Monday first, 'YYYY-MM-DD', in the
   venue's clock. ⚠️ "Mon" on a Saturday means the COMING Monday — the first
   version used this calendar week, so a Daily Deal set for next Monday read
   "Nothing on Mon." (3 Oct 2026, found by tapping it). */
function thisWeekDates(tz) {
  let ymd;
  try { ymd = new Intl.DateTimeFormat('en-CA', { timeZone: tz || undefined }).format(new Date()); }
  catch { ymd = new Intl.DateTimeFormat('en-CA').format(new Date()); }
  const [y, m, dd] = ymd.split('-').map(Number);
  const base = new Date(Date.UTC(y, m - 1, dd));
  const todayIdx = (base.getUTCDay() + 6) % 7;
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(base.getTime() + ((i - todayIdx + 7) % 7) * 864e5);
    return x.toISOString().slice(0, 10);
  });
}

function todayKey(tz) {
  try {
    return new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: tz || undefined })
      .format(new Date()).slice(0, 3).toLowerCase();
  } catch {
    return new Intl.DateTimeFormat('en-US', { weekday: 'short' })
      .format(new Date()).slice(0, 3).toLowerCase();
  }
}

/* ⚠️ THE ROW SHOWS TODAY, AND THE WEEK IS BEHIND THE TAP — the locked screen
 * reads `Today 12:00 PM – 11:30 PM`. Listing all seven days in the closed row
 * is what made the first version seven lines tall before anybody asked. */
function todayHours(hours, tz, special) {
  /* 130 — a dated change the restaurant set for today (a holiday, an early
     close) replaces the weekly hours, with the restaurant's own reason */
  const sp = (special || []).find((d) => d.on_date === nextDates(tz)[todayKey(tz)]);
  if (sp) {
    const why = sp.note ? ` · ${sp.note}` : '';
    const w = sp.is_closed ? null : dayHours(sp.windows);
    return w ? `Today ${w}${why}` : `Closed today${why}`;
  }
  const t = dayHours(hours && hours[todayKey(tz)]);
  return t ? `Today ${t}` : (hours && Object.keys(hours).length ? 'Closed today' : null);
}

/* each weekday's next date from today, in the venue's clock: { mon: 'YYYY-MM-DD', … } */
/* 170 — "Temporarily closed" (Google's and Yelp's own words) until the date
   the restaurant set on its Hours screen, in its own calendar */
function closedUntil(v) {
  if (!v || !v.closed_until) return null;
  let today;
  try { today = new Intl.DateTimeFormat('en-CA', { timeZone: v.timezone || undefined }).format(new Date()); }
  catch { today = new Intl.DateTimeFormat('en-CA').format(new Date()); }
  if (v.closed_until < today) return null;
  const [y, m, d] = v.closed_until.split('-').map(Number);
  const when = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return `Temporarily closed until ${when}`;
}

/* 126 — "Established 1975", the year only, as restaurants write it (Al-Noor,
   16 Oct 2026: "Don't worry about the month. Just established and 1975") */
function establishedLine(v) {
  const y = Number(v && v.year_established);
  if (!Number.isInteger(y) || y < 1800) return null;
  const l = uiLang();
  if (l === 'zh-HK') return `創立於${y}年`;
  if (l === 'zh-CN') return `创立于${y}年`;
  if (l === 'fr-CA') return `Fondé en ${y}`;
  return `Established ${y}`;
}

/* 199 — bring your own, and the dress code (Al-Noor, 16 Oct 2026), in the
   reader's language; the industry's own words (corkage 開瓶費, cake-cutting
   切餅費, OpenTable's dress codes). Nothing is shown for what was not answered. */
const BYO_WORDS = {
  en: { corkSome: 'Allowed, corkage charged', cutSome: 'Allowed, cake-cutting fee charged', drinks: 'Bring your own wine', cake: 'Bring your own cake', dress: 'Dress code', no: 'Not allowed', free: 'Allowed, no charge',
    cork: (p) => `Corkage ${p} a bottle`, cut: (p) => `Cake-cutting fee ${p}`,
    codes: { casual: 'Casual', smart_casual: 'Smart casual', business_casual: 'Business casual', jacket_preferred: 'Jacket preferred', formal: 'Formal' } },
  'zh-HK': { corkSome: '歡迎，另收開瓶費', cutSome: '歡迎，另收切餅費', drinks: '自攜酒水', cake: '自攜蛋糕', dress: '衣著要求', no: '不接受', free: '歡迎，免收費用',
    cork: (p) => `開瓶費每支 ${p}`, cut: (p) => `切餅費 ${p}`,
    codes: { casual: '休閒', smart_casual: '時尚便服', business_casual: '商務便服', jacket_preferred: '建議穿西裝外套', formal: '正式服裝' } },
  'zh-CN': { corkSome: '欢迎，另收开瓶费', cutSome: '欢迎，另收切饼费', drinks: '自带酒水', cake: '自带蛋糕', dress: '着装要求', no: '不接受', free: '欢迎，免收费用',
    cork: (p) => `开瓶费每瓶 ${p}`, cut: (p) => `切饼费 ${p}`,
    codes: { casual: '休闲', smart_casual: '时尚便装', business_casual: '商务便装', jacket_preferred: '建议穿西装外套', formal: '正式服装' } },
  'fr-CA': { corkSome: 'Permis, droit de bouchon exigé', cutSome: 'Permis, frais de service exigés', drinks: 'Apporter son vin', cake: 'Apporter son gâteau', dress: 'Code vestimentaire', no: 'Non permis', free: 'Permis, sans frais',
    cork: (p) => `Droit de bouchon de ${p} la bouteille`, cut: (p) => `Frais de service du gâteau de ${p}`,
    codes: { casual: 'Décontracté', smart_casual: 'Tenue de ville', business_casual: 'Tenue d’affaires décontractée', jacket_preferred: 'Veston recommandé', formal: 'Tenue de soirée' } },
};
function byoLines(v) {
  if (!v) return [];
  const W = BYO_WORDS[uiLang()] || BYO_WORDS.en;
  const cur = v.market === 'HK' ? 'HK$' : '$';
  const money = (x) => (x != null && Number.isFinite(Number(x)) ? cur + priceText(x) : null);
  const out = [];
  const say = (kind, fee, cost, some) => (kind === 'no' ? W.no : kind === 'free' ? W.free
    : kind === 'fee' ? (money(fee) ? cost(money(fee)) : some) : null);
  /* no bring-your-own line — Al-Noor, 22 Oct 2026 (cake-cutting gone 21 Oct) */
  const code = v.dress_code === 'other' ? (v.dress_code_other || null) : (v.dress_code ? W.codes[v.dress_code] : null);
  if (code) out.push([W.dress, code]);
  return out;
}

function nextDates(tz) {
  let ymd;
  try { ymd = new Intl.DateTimeFormat('en-CA', { timeZone: tz || undefined }).format(new Date()); }
  catch { ymd = new Intl.DateTimeFormat('en-CA').format(new Date()); }
  const [y, m, dd] = ymd.split('-').map(Number);
  const base = Date.UTC(y, m - 1, dd);
  const out = {};
  for (let i = 0; i < 7; i++) {
    const x = new Date(base + i * 864e5);
    out[['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][x.getUTCDay()]] = x.toISOString().slice(0, 10);
  }
  return out;
}

/* ⚠️ WHERE THIS VISIT CAME FROM — 3 Oct 2026, for Restaurant Analytics'
 * "How people found you on Eleat" and the carousel page. The card that was
 * tapped puts `from` (home · search · explore · view_all · saved · zaryna)
 * and, on a Home rail, `rail` + `slot` in the address. A venue opened from a
 * link somebody sent has none of them: that is "link". */
function cameFrom() {
  const q = new URLSearchParams(window.location.search);
  const from = q.get('from') || 'link';
  const out = { source_screen: from };
  /* the words that found this page (ui.jsx carries them from Explore) */
  if (from === 'search' && (q.get('sq') || '').trim()) out.search_query = q.get('sq').trim().slice(0, 120);
  const slot = Number(q.get('slot'));
  if (from === 'home' && q.get('rail') && slot >= 1 && slot <= 50) {
    out.carousel_type = 'home';
    out.carousel_slot = slot;
    out.metadata = { surface: q.get('rail') };
  }
  return out;
}

export default function Venue({ id, tab: initialTab = 'about', onBack }) {
  /* ⚠️ IT OPENS ON THE TAB THAT WAS TAPPED, NEVER ON ABOUT. Somebody who taps
   * a Happy Hour card wants Happy Hour. Landing them on About and making them
   * find it again is the fault task #101 exists for. */
  const [tab, setTab]       = useState(initialTab);
  /* the dish a tap opened — see ItemSheet and OPEN_ITEM below */
  const [item, setItem] = useState(null);
  useEffect(() => { countVenueView(); }, [id]);
  useEffect(() => {
    const open = (e) => setItem(e.detail || null);
    window.addEventListener('eleat:item', open);
    return () => window.removeEventListener('eleat:item', open);
  }, []);

  const [v, setV]           = useState(null);
  const [err, setErr]       = useState(null);
  const [saveErr, setSaveErr] = useState(null);
  const [saved, setSaved]   = useState(false);
  const [hoursOpen, setHoursOpen] = useState(false);
  /* 226 · Google's stars + reviews until Eleat has five of its own — asked once
     the venue is in hand, and only when Eleat's own rating is not shown */
  const [google, setGoogle] = useState(null);

  /* ⚠️ A HOOK, AND IT SITS WITH THE OTHER STATE ON PURPOSE. Everything this
   * screen reads must be declared above the effects that read it — the
   * temporal dead zone note twenty lines down is what happens otherwise. */
  const centre = useCentre();
  /* ⚠️ WHERE THE PHONE IS — the point "how far is it" is measured from. See
     the note above `distanceKm`. */
  const device = useDevice();

  /* ⚠️ THE FILTERS ARE STATE ON THE PAGE, NOT INSIDE EACH PANE — because they
   * have to survive a tab change. An Eleater who filters Happy Hour to
   * Saturday, looks at About and comes back should still be on Saturday. Held
   * in the pane, the state would be thrown away with the unmounted component.
   * `null` on the day means TODAY, resolved at render against the venue's own
   * timezone, never the phone's. */
  const [cat, setCat]   = useState(null);   // menu category jump bar
  const [day, setDay]   = useState(null);   // deal day strip
  const [ffilt, setFfilt] = useState('all'); // feeds filter
  const [photo, setPhoto] = useState(0);    // which hero photo is showing
  /* the thumbnail being shown stays in view as the arrow walks the strip */
  const thumbsRef = useRef(null);
  useEffect(() => {
    const strip = thumbsRef.current;
    const el = strip && strip.children[photo];
    if (strip && el) strip.scrollTo({ left: el.offsetLeft - strip.offsetLeft - 8, behavior: 'smooth' });
  }, [photo]);

  /* one cache per tab, so a tab is fetched the first time it is opened and
   * not again — see the note in api.js about eight requests to show one */
  const [pane, setPane] = useState({});

  /* every live deal this venue has, in one call — see the rule above. null
     while it is still being asked for, so the tab strip waits rather than
     flashing four tabs and then removing three. */
  const [deals, setDeals] = useState(null);

  /* ══════════════════════════════════════════════════════════════════════
   * ⚠️⚠️ THIS EFFECT WAS ABOVE `const [v]` AND IT BROKE THE WHOLE VENUE PAGE
   *
   * Al, 23 September 2026, four screenshots and most of an evening:
   *   *"the app doesn't open up on my phone… nothing's happening."*
   *   *"same thing."*  *"same no change. done it 3 times now."*
   *   *"you've done this several times now and it's the same fucking thing."*
   *
   * The error his phone kept showing was **"Cannot access 'a' before
   * initialization"**, and this is it. The effect sat above the `useState`
   * lines, and its dependency array — `[tab, v, deals, id]` — is evaluated
   * DURING RENDER, on that line, before `const [v]` on the line below it and
   * `const [deals]` twenty lines further down had run. A temporal dead zone.
   * `v` minifies to `a`.
   *
   * ⚠️ IT WAS NEVER INTERMITTENT AND IT WAS NEVER HIS PHONE. **Every venue
   * page has been dead since this effect was written.** It looked
   * device-specific only because I kept testing Home, which does not render
   * this component — and every "cannot reproduce" I reported back to him was
   * me checking the one screen that could not show the fault.
   *
   * ⚠️ AND THE DEPENDENCY ARRAY IS THE TRAP. The body of an effect runs after
   * render and would have been fine; the ARRAY is an ordinary expression on an
   * ordinary line. A hook that reads state must sit below the state it reads,
   * every time, and no comment is going to be what stops that happening again
   * — `verify_no_tdz.js` now refuses it, including in destructured form, which
   * is the form React uses for every piece of state in the app.
   *
   * ── WHAT IT COUNTS (unchanged) ─────────────────────────────────────────
   *
   * Every tab open is a number a restaurant is shown. menu_opened is the
   * Signature Menu; the four deal tabs are deal_viewed. The registry requires
   * a deal_id on deal_viewed, so it fires only once the deals are loaded and
   * the one for that tab is known — an event with no deal_id counts nothing.
   *
   * ⚠️ AND THE FIRST TAB COUNTS TOO. Somebody who taps a Happy Hour card from
   * Home lands on Happy Hour without ever touching a tab; this catches that,
   * which a click handler never would.
   * ══════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    if (!v) return;
    if (tab === 'menu') { track('menu_opened', { restaurant_id: id, source_screen: 'venue' }); return; }
    if (tab === 'feeds') { track('feed_viewed', { restaurant_id: id, source_screen: 'venue' }); return; }
    const t = TABS.find((x) => x.key === tab);
    if (!t || !t.deal || !Array.isArray(deals)) return;
    const d = deals.find((x) => x && x.deal_type === t.deal);
    if (d) track('deal_viewed', { restaurant_id: id, deal_id: d.id, source_screen: 'venue' });
    /* Game Day's games come from 019's tables, not a deal row — counted
       here, and by game in GameDay.jsx when one is chosen */
  }, [tab, v, deals, id]);

  useEffect(() => {
    let live = true;
    api.get(id)
      .then((d) => {
        if (!live) return;
        setV(d);
        /* ⚠️ COUNTED WHEN THE VENUE LOADS, NOT WHEN A CARD IS TAPPED. A tap
           that never resolves is not a view, and a venue opened from a link
           somebody shared has no tap at all. This is the number a restaurant
           cares about most — how many people actually opened their page. */
        track('restaurant_viewed', { restaurant_id: id, ...cameFrom() });
      })
      .catch((e) => live && setErr(e.message));
    /* ⚠️ ONE CALL, TWO JOBS: which deal tabs exist, and what goes in them.
       It used to be four calls — one per type — fired only when a tab was
       opened, which is why the strip could not know what to draw. */
    api.deals(id)
      .then((d) => live && setDeals(Array.isArray(d) ? d : []))
      .catch(() => live && setDeals([]));
    /* ⚠️ THE HEART IS ONLY ASKED ABOUT WHEN SOMEBODY IS SIGNED IN. The route
     * needs auth; asking while signed out is a guaranteed 401 in the console
     * and tells us nothing. Signed out, the heart is simply empty. */
    if (getToken()) api.isSaved(id).then((d) => live && setSaved(!!d.saved)).catch(() => {});
    return () => { live = false; };
  }, [id]);
  /* the page's own heart follows a save made anywhere else — the Feed
     viewer's Save left it empty until a reload (audit, 7 Oct 2026) */
  const savedSet = useSaved();
  const savedFirst = useRef(true);
  useEffect(() => {
    if (savedFirst.current) { savedFirst.current = false; return; }
    setSaved(savedSet.has(id));
  }, [savedSet, id]);

  /* ⚠️ THE LIT TAB IS ALWAYS IN VIEW — look walk, 9 Oct 2026. A card opens the
     venue at the tab it was about (Happy Hour, Feeds, Reviews…), and on a
     phone those sit off the right of the strip, so the page opened with no tab
     looking chosen. Slides the STRIP sideways only — scrollIntoView would also
     move the page. */
  const tabsRef = useRef(null);
  useEffect(() => {
    const strip = tabsRef.current;
    const on = strip && strip.querySelector('.tab.active');
    if (!on) return;
    const left = on.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
    if (left < strip.scrollLeft || left + on.offsetWidth > strip.scrollLeft + strip.clientWidth) {
      strip.scrollLeft = Math.max(0, left - 16);
    }
  }, [tab, v, deals]);

  useEffect(() => {
    if (pane[tab] !== undefined) return;
    const t = TABS.find((x) => x.key === tab);
    /* ⚠️ A DEAL TAB IS ALREADY IN HAND. Asking again for what the one call on
       mount returned would be a second answer to the same question — and the
       two could disagree the moment a deal expires between them. */
    /* ⚠️ EVERY DEAL TAB EXCEPT GAME DAY IS ALREADY IN HAND — see the note
       below. Game Day needs a second answer (the games), so it is excepted
       here by name rather than by loosening the rule for all four. */
    if (t && t.deal && tab !== 'gd') return;
    const load =
      /* ⚠️ THE SIGNATURE MENU IS THE RESTAURANT'S CHOSEN DISHES, NOT THE
         WHOLE MENU — Al, 3 Oct 2026. /menu/restaurant answers every active
         dish (the admin's Item Library reads the same route); this tab shows
         the ones marked is_everyday_menu, in everyday_rank order, which is
         the order the server already returns them in. */
      tab === 'menu'    ? api.menu(id).then((d) =>
          (Array.isArray(d) ? d : (d && d.items) || []).filter((m) => m.is_everyday_menu))
      : tab === 'feeds'   ? api.feeds(id)
      : tab === 'reviews' ? api.reviews(id)
      /* ⚠️ GAME DAY IS THE ONE DEAL TAB THAT ASKS FOR MORE, and the `t.deal`
         early-return above is why this line sits BELOW it — `gd` carries
         `deal: 'game_day'`, so without the exception a few lines up this
         request would never be made at all. The deals are already in hand;
         what is missing is what is PLAYING. */
      : tab === 'gd'      ? api.gameDay(id)
      : null;
    if (!load) return;
    let live = true;
    /* ⚠️ A FAILED TAB SAYS SO. It does not sit on a spinner for ever and it
     * does not pretend the venue has nothing — "nothing here" and "we could
     * not ask" are different sentences and an Eleater deserves the right one. */
    load.then((d) => live && setPane((p) => ({ ...p, [tab]: d })))
        .catch((e) => live && setPane((p) => ({ ...p, [tab]: { error: e.message } })));
    return () => { live = false; };
  }, [tab, id]);

  /* ⚠️ ONE PAID LOOKUP PER PAGE OPENED, and none once Eleat has five verified
     reviews (the server checks that too). A failure draws nothing — Google's
     section is extra, never a reason for the page to look broken. */
  const eleatRated = v ? Boolean(v.rating_shown) : null;
  useEffect(() => {
    setGoogle(null);
    if (eleatRated !== false) return;
    let live = true;
    api.googleReviews(id, uiLang())
      .then((d) => live && d && d.show && setGoogle(d))
      .catch(() => {});
    return () => { live = false; };
  }, [id, eleatRated]);

  /* ⚠️ A WAY OUT — walked live, 12 Oct 2026: an old or shared link to a
     restaurant no longer on Eleat drew one red line and nothing else (no back,
     no tab bar). It says so plainly and offers Explore. */
  if (err) {
    return (
      <div className="home">
        <div className="top"><h1 className="heroline">Restaurant</h1></div>
        <main>
          <p className="mut">{/not found/i.test(err) ? 'This restaurant is not on Eleat any more.' : err}</p>
          <div className="guest"><a className="gcta" href="?explore=1">Explore restaurants</a></div>
        </main>
        <TabBar />
      </div>
    );
  }
  /* ⚠️ `.mut`, the same quiet grey the Doorway waits in — NOT the section
     heading. This line wore the heading class until 18 September and rendered
     "L O A D I N G" in spaced capitals: a styling accident that reads like a
     decision. A waiting message and a section title are different things and
     must not share a class. */
  if (!v)  return <div className="screen"><p className="mut">Loading…</p></div>;
  setVenueClock(v.time_format);   /* 151 — before any time below is written */

  const hours = v.operating_hours || {};

  /* ── WHICH TABS THIS VENUE HAS ────────────────────────────────────────────
     A deal tab appears only if the venue has a published deal of that type;
     About, Signature Menu, Feeds and Reviews always appear. The long note
     beside TABS says why, and quotes Al on both halves.

     ⚠️ WHILE `deals` IS STILL null WE SHOW THE NON-DEAL TABS ONLY. Drawing
     all four and then removing three is a page that rearranges itself under
     somebody's thumb — and the one they were reaching for may be the one that
     goes. Tabs arrive; they never leave. */
  const gdExtra = { gameDay: !!(v && v.has_game_day) };
  const shownTabs = tabsFor(deals, gdExtra);

  /* ⚠️ AND IF THE OPEN TAB IS NOT ONE THIS VENUE HAS, FALL BACK TO ABOUT.
     `?tab=gd` can be typed, shared in a link, or left over from the venue
     looked at before this one. Without this the page renders a tab strip that
     does not contain the selected tab and a pane with nothing in it. */
  const activeTab = openTab(deals, tab, gdExtra);

  /* ⚠️ ABOUT IS THE FULL PAGE; EVERY OTHER TAB IS COMPACT. Straight from the
   * locked screen: `.screen.compact` hides the hero and the identity block and
   * shows the slim `.idcard` instead, moving the name and Reserve up into the
   * bar. Without it, tapping Happy Hour means scrolling past a 200px
   * photograph to reach a price list. */
  const compact = tab !== 'about';

  /* ⚠️⚠️ THIS ROUNDED, AND AL NAMED THAT FAULT BY NUMBER. `Math.round(4.5)` is
   * 5, so a venue rated 4.5 drew FIVE FULL STARS — *"if a review says 4.5 out
   * of 5 then why give it a 5 star. it should be 4.5 stars. NOT 5."*  It now
   * uses the same `Stars` every other rating in Eleat uses.
   * ⚠️ AND `rating_shown` STILL GATES IT — nothing draws until the fifth
   * eligible review, so this can never show a rating that was not earned. */
  const stars = v.rating_shown ? <Stars value={v.rating} /> : null;

  /* ⚠️ `Upscale · Italian · Central` — price band, then kind, then area, in
   * that order, from the locked markup's `.pricetype`. The <b> is deliberately
   * NOT bolder (the locked rule sets `.pricetype b{font-weight:400}`): it marks
   * the price band as a different kind of fact without shouting it. */
  /* ⚠️ THE SERVER SENDS THE NAME; THIS SCREEN NEVER MAKES ONE UP. Every label
   * below is taxonomy_terms.name_en or cuisines.name_en, read from the
   * database by restaurants.service.js. `pretty()` is gone — it turned
   * dim_sum into "Dim sum" when the venue's actual words are
   * "Dim Sum / Yum Cha", and a key the schema marks "never displayed" was
   * being displayed on every venue in Eleat.
   *
   * ⚠️ AND name_zh TRAVELS BESIDE IT. Not read yet — the screen has no locale
   * switch — but it arrives, so Cantonese and Mandarin are now a change to
   * this line rather than a change to the database. */
  /* ⚠️ …AND NOW IT IS READ — 5 Oct 2026. A Chinese reader gets the *_zh
     name (Simplified for Mandarin), anyone else the English; never nothing. */
  const bandName = priceBand(v);
  const areaName = zh(v.area, v.area_zh);
  const typeLine = [zh(v.venue_type_en, v.venue_type_zh), areaName].filter(Boolean).join(' · ');
  /* the restaurant's name: its Chinese name first for a Chinese reader (as
     OpenRice shows it), the other one beside it */
  const nameMain = zh(v.name, v.name_zh);
  const nameSub = v.name_zh ? (nameMain === v.name ? v.name_zh : v.name) : null;
  const cuisines = Array.isArray(v.cuisines) ? v.cuisines : [];
  const facilities = Array.isArray(v.facilities) ? v.facilities : [];
  /* 169 — what the restaurant added itself, after Eleat's own terms */
  const ownFac = ownFacilities(v);

  /* ⚠️ THE PIN GIVES DIRECTIONS, IT DOES NOT SEARCH — the locked screen uses
   * `/maps/dir/?api=1&destination=…`, and mine used `/maps/search/`. Search
   * drops somebody on a map looking at a marker; directions is what a person
   * standing there with a phone actually wants.
   * ⚠️ AND IT IS THE COORDINATE OR IT IS NOTHING — 25 September 2026. This
   * comment used to end "the written address when we do not"; it no longer
   * does. In Lan Kwai Fong or inside a mall the words land you on the street
   * rather than at the door, and a Directions control that drops somebody a
   * block away is the same class of promise as a table Eleat never booked. */
  /* ⚠️ HOW FAR IT IS IS MEASURED HERE, BECAUSE THE ROUTE DOES NOT SEND IT.
   * `GET /restaurants/:id` → `getById()` in restaurants.service.js selects
   * location_lat and location_lng and computes NO distance_km; only the
   * discovery search does (`${distance} as distance_km`). Read on 25 September
   * 2026, in the file, not remembered. So it is worked out from the Eleater's
   * own centre with place.js's own `metresBetween` — the SAME crow-flies
   * measurement, not a second definition of what distance means.
   *
   * ⚠️ AND IT IS A DISTANCE, NEVER A TRAVEL TIME. `howFar()` says "away" and
   * nothing here turns it into minutes; directions.js explains why in full.
   * No centre, or no coordinates, and there is simply no line. */
  /* ══════════════════════════════════════════════════════════════════════
     ⚠️⚠️⚠️ MEASURED FROM WHERE THEY ARE STANDING, NOT FROM WHAT THEY ARE
     BROWSING — AL, 27 SEPTEMBER 2026.

     *"even if I've chosen Hong Kong and I'm sitting in Futian, I still want to
     be able to move around… what if I was in Nanshan or Shekou or Bao'an and I
     wanted to know where a restaurant is and how far it is? So it always
     clicks exactly where the fuck I'm standing. Even if the home is Hong Kong,
     it's where I'm fucking standing. And then Google will pick it up and take
     me there and say it's 27, 30 kilometers, 40 kilometers away."*

     ⚠️ THIS MEASURED FROM `centre`, AND THAT PRODUCED A CONFIDENT LIE. Browsing
     Hong Kong from Futian, every venue read a few hundred metres away while he
     was 27 km from all of them — and Zaryna's own answer said *"0.4 km away in
     Mong Kok"* on his screen. Nothing threw. A wrong number renders exactly as
     happily as a right one.

     ⚠️ TWO POINTS, TWO JOBS, AND THEY ARE NOT INTERCHANGEABLE — this is the
     same split the rest of the app already makes:
       · the CENTRE decides what is SEARCHED — Al's own rule, unchanged:
         *"It must NOT measure 8 km from Vancouver merely because the phone is
         physically there."*
       · the DEVICE decides HOW FAR IT IS, because that question is only ever
         about the person asking it.

     ⚠️ AND IT ALREADY AGREES WITH DIRECTIONS. `directions.js` deliberately
     sends Google NO origin so the route starts from the phone — so the number
     on the row and the route behind it finally describe the same journey.
     Before this they could differ by the width of a border.

     ⚠️ THE CENTRE IS THE FALLBACK, NEVER THE FIRST CHOICE. A phone that has
     not reported yet still shows something reasonable rather than nothing, and
     with neither there is no line at all. */
  const from = (device && Number.isFinite(device.lat)) ? device : centre;
  const distanceKm = (from && Number.isFinite(Number(from.lat)) && canRoute(v))
    ? metresBetween(
        { lat: Number(from.lat), lng: Number(from.lng) },
        { lat: Number(v.location_lat), lng: Number(v.location_lng) },
      ) / 1000
    /* ⚠️ `undefined`, NEVER `null`, AND THE GATE CAUGHT ME ON IT.
     * `howFar()` and `defaultMode()` both start `Number(distanceKm)`, and
     * `Number(null)` is **0** — a finite, non-negative zero. So a null would
     * have printed **"0 m away"** on the address row of every venue Eleat has
     * no centre for, and opened Maps on walking for a place across the
     * harbour. `Number(undefined)` is NaN, which both of them refuse.
     * Nothing throws either way; it is a confident wrong sentence. */
    : undefined;
  /* ⚠️ THE ERROR TRAVELS WITH THE DISTANCE — and ONLY when the distance was
     measured from the phone. A chosen place has no error: somebody who picked
     Central is exactly in Central, so passing an accuracy there would suppress
     a number that is perfectly true. See the note on `howFar`. */
  const farAway = howFar(
    distanceKm,
    from === device ? (device && device.accuracy_m) : null,
  );

  /* ⚠️ NO COORDINATES, NO CONTROL. `directionsHref` answers null and the
   * address below is drawn as plain text instead of a link — a Directions
   * control that cannot route anybody anywhere is the dead control this
   * project keeps clearing out.
   *
   * ⚠️ THIS IS A DEPARTURE FROM `11_Restaurant.html`, WHICH FALLS BACK TO THE
   * WRITTEN ADDRESS ("a venue with no coordinates still gets directions").
   * Al's instruction of 25 September is precedence #1 and asks for the
   * opposite; recorded here so nobody restores it as a fix. */
  /* ⚠️⚠️ THE CENTRE USED TO BE PASSED HERE AS THE ORIGIN, AND IT WAS WRONG.
   * Browsing Calgary from Vancouver, that told Google the journey begins in
   * Calgary. `directions.js` now sends no origin at all, so Google starts from
   * the device's own position — see that file for Al's instruction in full.
   * The centre is still what `distanceKm` above is measured from, because that
   * is a different question: how far is this from what I am looking at. */
  /* the phone's own fix (Al, 6 Oct 2026) — the same one "km away" uses */
  const mapHref = directionsHref(v, defaultMode(distanceKm), device);

  /* ⚠️ THE COVER IS THE FIRST PHOTO, NOT A SEPARATE ONE. The locked screen's
   * carousel opens on the same image the card that got you here was showing —
   * so `cover_url` leads and `gallery_urls` follows, with the cover filtered
   * out of the gallery if the two overlap. A venue whose first thumbnail is a
   * different picture from the one that was just on screen reads as the wrong
   * venue for a moment.
   *
   * ⚠️ AND IT SURVIVES AN EMPTY GALLERY. `gallery_urls` was written by nothing
   * until today, so most rows still have none; the filter and the guard below
   * mean those venues show their single cover and no carousel controls,
   * exactly as before. */
  const photos = [v.cover_url, ...(Array.isArray(v.gallery_urls) ? v.gallery_urls : [])]
    .filter(Boolean)
    .filter((src, i, all) => all.indexOf(src) === i);

  /* ══════════════════════════════════════════════════════════════════════
     ⚠️⚠️ SHARE SITS BESIDE SAVE, AND IT HAD NEVER REACHED REACT.
     `11_Restaurant.html` line 361 puts both inside `.heroacts`:
       <button class="save" id="save">♡ Save</button>
       <button class="save" id="shareVenue" data-share …>{plane} Share</button>
     Same `.save` class, so it is the same 38px circle — one control's worth of
     styling, not a second design. Every string comes from `share.js`, which
     quotes the locked files.

     ⚠️ AND IT IS DRAWN IN BOTH PLACES THE HEART IS, for a reason that is only
     visible today: the hero renders ONLY when the venue has a photograph, and
     **every venue's `cover_url` is null right now** while Al waits on a usable
     set. Put Share in the hero alone and it would be invisible on every venue
     in Eleat — and would look, to him, exactly like it had not been built.
     The heart already solved this by appearing in `.idc-top` as well. */
  const shareBtn = (extraClass, withWord = false) => (
    <button
      className={`save ${extraClass}`}
      type="button"
      aria-label="Share"
      onClick={() => openShare({
        ...shareVenue(v),
        venueId: id,
        subjectType: 'restaurant',
      })}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round"
        aria-hidden="true">
        {SHARE_PATHS.map((d) => <path key={d} d={d} />)}
      </svg>
      {/* `#shareVenue` on the photo reads "Share" beside its icon */}
      {withWord ? ' Share' : null}
    </button>
  );

  const heart = (extraClass, withWord = false) => (
    <button data-i18n-ctx="state"
      className={saved ? `save on ${extraClass}` : `save ${extraClass}`}
      type="button"
      aria-pressed={saved}
      aria-label={saved ? 'Saved' : 'Save'}
      onClick={() => {
        if (needsAccount('save', saved ? null : { save: id })) return;
        /* ⚠️ AND TELL EVERY CARD, NOT JUST THIS BUTTON. Without markSaved an
           Eleater hearts a place here, taps back to Home, and the card they
           just saved still shows an empty heart until a full reload — one
           fact held in two places, and the copy nobody is looking at is
           always the one that goes stale. */
        /* ⚠️ WHICH CALL DEPENDS ON THE CURRENT STATE. Always POSTing meant
           the heart could be filled and never emptied. */
        (saved ? api.unsave(id) : api.save(id))
          .then((d) => {
            /* DELETE answers {saved:false}; POST answers {saved:true}. Trust
               the server's answer rather than assuming the flip worked. */
            const now = d && typeof d.saved === 'boolean' ? d.saved : !saved;
            setSaved(now); markSaved(id, now);
          })
          /* a failed save says so for a moment — it never replaces the page */
          .catch((e) => { setSaveErr(e.message); setTimeout(() => setSaveErr(null), 5000); });
      }}
    >
      <span className="hh"><HeartIcon on={saved} /></span>
      {/* on the photo the locked button carries its word — `#save`:
          "♡ Save" / "♥ Saved"; the slim card's `#save2` is the heart alone */}
      {withWord ? (saved ? ' Saved' : ' Save') : null}
    </button>
  );

  /* ⚠️ RESERVE IS REAL OR IT IS NOT THERE. The locked screen shows
   * "Reserve a Table" on every venue, but the venue route returns no booking
   * link for a venue that has none, and a button that does nothing is the dead
   * control this project keeps clearing out. So it renders only when there is
   * somewhere to send them — and the Eleater goes to the restaurant's own
   * provider, which is exactly what the Privacy Policy says Eleat does:
   * "Eleat hands the Eleater to the provider's page". */
  /* ⚠️ Al-Noor, 4 Nov 2026: "Every fucking restaurant on this goddamn earth
     has a reservation. And the ones that don't will actually say, we don't
     have reservation." Reserve shows on every restaurant; only a Walk-in only
     one (Core identity's own words) says so instead. Test venues with no page
     of their own open Eleat's mock reservation page (229). */
  const walkInOnly = v.takes_reservations === false;
  const reserveHref = walkInOnly ? null : (v.reservation_url ? 'reserve'
    : v.phone ? `tel:${String(v.phone).replace(/\s+/g, '')}` : 'reserve');
  /* the offer on screen when Reserve is tapped — Analytics' "Reservation
     clicks" per Happy Hour / Daily Deal / Last-Minute (3 Oct 2026) */
  const tabDeal = (() => {
    const t = TABS.find((x) => x.key === activeTab);
    return t && t.deal && Array.isArray(deals)
      ? deals.find((d) => d && d.deal_type === t.deal) || null : null;
  })();
  const reserveLink = `?reserve=${encodeURIComponent(id)}`
    + (tabDeal ? `&deal=${encodeURIComponent(tabDeal.id)}` : '');


  return (
    <div className={compact ? 'screen venue compact' : 'screen venue'}>
      {item && <ItemSheet item={item} venue={v} onClose={() => setItem(null)} />}

      {/* ── the bar that stays put ─────────────────────────────────────── */}
      <div className="topbar">
        <button className="back" type="button" onClick={onBack} aria-label="Back">‹</button>
        <div className="tbtitle">
          <div className="tbname">{nameMain}</div>
          <div className="tbsub">{[bandName, typeLine].filter(Boolean).join(' · ')}</div>
        </div>
        {reserveHref && (
          <a className="reserveTop" href={reserveHref === 'reserve' ? reserveLink : reserveHref}
            onClick={(ev) => { if (needsAccount('reserve', { ret: ev.currentTarget.getAttribute('href') })) ev.preventDefault(); }}>
            Reserve
          </a>
        )}
      </div>

      {/* ── the photograph ─────────────────────────────────────────────────
          ⚠️ NO PHOTOGRAPH, NO FRAME. Every venue's `cover_url` is null today —
          the research package's images are almost all reference-only and Al is
          waiting on a usable set. An empty 200px bordered box on every venue
          would look like a broken image rather than an absent one, so the hero
          simply is not drawn until there is something to put in it. The moment
          a cover arrives this appears, with the heart and share already on it. */}
      {photos.length > 0 && (
        <div className="hero">
          {/* `11_Restaurant.html .heroacts` holds BOTH, in this order */}
          <div className="heroacts">{heart('', true)}{shareBtn('', true)}</div>
          <img className="bg" src={photos[photo] || photos[0]} alt={v.name} />
          <div className="scrim" />

          {/* ⚠️ THE HERO IS A CAROUSEL ON THE LOCKED SCREEN AND WAS ONE PHOTO
            * HERE. `11_Restaurant.html` carries a strip of four thumbnails
            * bottom-left and a next arrow bottom-right, and its own script
            * swaps the big image and moves the `active` class. That is copied
            * exactly, not reinterpreted — no dots, no auto-advance, no swipe
            * gesture. Inventing any of those would be inventing Eleat.
            *
            * ⚠️ AND IT ONLY APPEARS WITH SOMETHING TO SCROLL THROUGH. A single
            * photograph draws no strip and no arrow — a carousel control over
            * one image is a control that does nothing. */}
          {photos.length > 1 && (
            <div className="heroStrip">
              <div className="thumbs" ref={thumbsRef}>
                {photos.map((src, i) => (
                  <div key={src + i}
                    className={'thumb' + (i === photo ? ' active' : '')}
                    onClick={() => setPhoto(i)}>
                    <img src={src} alt="" loading="lazy" />
                  </div>
                ))}
              </div>
              <button className="navArrow" title="Next photo"
                onClick={() => setPhoto((photo + 1) % photos.length)}>›</button>
            </div>
          )}
        </div>
      )}

      {/* ── who this place is ──────────────────────────────────────────── */}
      <div className="info">
        <div className="idHead">
          <div className="idLeft">
            <h1 className="restName">{nameMain}{nameSub && <span className="restNameZh">{nameSub}</span>}</h1>
            {/* the venue's Chinese name beside its name, the OpenRice way (136,
                Al 4 Oct 2026) — only when the venue has one.
                the About page's "Short tagline", under the name — Al, 4 Oct 2026:
                restaurants fill it in and it showed nowhere */}
            {aboutText(v).tagline && <div className="restTag">{aboutText(v).tagline}</div>}
            {/* ⚠️ NO NUMBER UNTIL IT IS EARNED — the server sends rating null
                below five eligible reviews, so the count stands alone rather
                than a bare star with nothing after it. */}
            <div className="ratingRow">
              {v.rating_shown ? (
                <>
                  <span className="stars">{stars}</span>
                  {Number(v.rating).toFixed(1)}
                  <span className="mut">· ({v.review_count})</span>
                </>
              ) : google ? (
                <GoogleStars g={google} />
              ) : (
                <span className="mut">{v.review_count === 0
                  ? 'No reviews yet'
                  : `${v.review_count} review${v.review_count === 1 ? '' : 's'}`}</span>
              )}
            </div>
            <div className="pricetype">
              {bandName && <b>{bandName}</b>}
              {bandName && typeLine ? ' · ' : ''}
              {typeLine}
            </div>
          </div>
        </div>

        <div className="tagRow">
          {cuisines.slice(0, 3).map((c) => (
            <span key={c.key} className="tag">{label(c)}</span>
          ))}
        </div>

        {walkInOnly && <p className="walkinonly">Walk-in only</p>}
        {reserveHref && (
          <a className="reserveWide" href={reserveHref === 'reserve' ? reserveLink : reserveHref}
            onClick={(ev) => { if (needsAccount('reserve', { ret: ev.currentTarget.getAttribute('href') })) ev.preventDefault(); }}>
            Reserve a Table
          </a>
        )}

        {/* ── hours · location · contact ──────────────────────────────── */}
        <div className="logi">
          <button
            className={hoursOpen ? 'lrow hoursrow open' : 'lrow hoursrow'}
            type="button"
            onClick={() => setHoursOpen((o) => !o)}
          >
            <div className="lgrow">
              <div className="lk">Hours <span className="hint-tap">tap for the week</span></div>
              <div className="lv">
                {/* ⚠️ THE CHEVRON IS DRAWN, NEVER SPELLED. The locked screen
                    used U+2304; Montserrat does not carry it, so the browser
                    substituted a font that drew it rotated and it read as a
                    right-pointing arrow for weeks. An SVG cannot be
                    substituted. */}
                <span className="hrschev" aria-hidden="true">
                  <svg viewBox="0 0 12 8" width="12" height="8">
                    <path d="M1 1.5 L6 6.5 L11 1.5" fill="none"
                          stroke="currentColor" strokeWidth="1.8"
                          strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                {closedUntil(v) || todayHours(hours, v.timezone, v.special_hours) || 'Hours not listed yet'}
              </div>
              {hoursOpen && (
                <div className="hrs-list">
                  {DAYS.map(([k, lbl]) => {
                    /* 130 — this weekday's coming date, if the restaurant changed it */
                    const sp = (v.special_hours || []).find((d) => d.on_date === nextDates(v.timezone)[k]);
                    const t = sp ? ((sp.is_closed ? null : dayHours(sp.windows)) || 'Closed') : (dayHours(hours[k]) || 'Closed');
                    return (
                      <div key={k} className={k === todayKey(v.timezone) ? 'hrs-r today' : 'hrs-r'}>
                        <span className="hrs-d">{lbl}</span>
                        <span className="hrs-t">{t}{sp && sp.note ? ` · ${sp.note}` : ''}</span>
                      </div>
                    );
                  })}
                  {/* ⚠️ THE ZONE IS NAMED. Times are stored UTC and shown in the
                      venue's own IANA zone; saying which one removes the
                      question for anybody reading this from Calgary. */}
                  {v.timezone && <div className="hrs-tz">Times shown in {v.timezone.replace('_', ' ')}.</div>}
                </div>
              )}
            </div>
          </button>

          {mapHref ? (
            /* ⚠️ `noopener` AS WELL AS `noreferrer`. A new tab opened without it
               keeps a handle on this one and can navigate it somewhere else. */
            <a className="locrow link" href={mapHref} target="_blank" rel="noopener noreferrer"
              onClick={() => track('directions_tapped', { restaurant_id: v.id, source_screen: 'venue' })}>
              <span className="lpin" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="15" height="19">
                  <path d="M12 1.5c-4 0-7 3-7 6.9 0 5 7 13.6 7 13.6s7-8.6 7-13.6c0-3.9-3-6.9-7-6.9z"
                        fill="currentColor" />
                  <circle cx="12" cy="8.4" r="2.6" fill="var(--bg)" />
                </svg>
              </span>
              {/* ══════════════════════════════════════════════════════════
                  ⚠️⚠️ IT SAYS **Directions** WHEN IT IS ONE, AND Location WHEN
                  IT IS NOT — AND THE DIFFERENCE IS TRUE, NOT CosMETIC.
                  Al, 26 September 2026: *"That's what I see with Google when I
                  go to a restaurant. It says directions. And that's what we
                  should be doing… we're just giving the address and a map
                  balloon and so on. But we're not saying directions on there."*

                  He is right. This row has opened Google Maps into turn-by-turn
                  since the 25th and **never told anybody it would.** A control
                  nobody can see is a control nobody uses.

                  ⚠️ AND IT IS STILL THE ADDRESS ROW, NOT A SECOND BUTTON. The
                  locked `11_Restaurant.html` deliberately removed a separate
                  "Directions ›" in August because it competed with the address.
                  The word goes ON the row that already does the job.

                  ⚠️ THE OTHER BRANCH BELOW KEEPS "Location" and that is the
                  honest word there: with no coordinates there is nothing to
                  route to, so it is an address and not a way to get anywhere.
                  Promising Directions and opening nothing is worse than silence.
                  ══════════════════════════════════════════════════════════ */}
              <div className="lgrow">
                <div className="lk">Directions</div>
                <div className="la">{v.formatted_address || v.address}</div>
              </div>
              {/* ⚠️ `.km` IS THE LOCKED SCREEN'S OWN CLASS for this number —
                  `11_Restaurant.html` right-aligns `<span class="km">0.6 km</span>`
                  in this same logistics block. The WORDS are howFar()'s, which
                  add "away" so the number can never be read as a journey time. */}
              {farAway && <span className="km">{farAway}</span>}
            </a>
          ) : v.address ? (
            <div className="locrow">
              <span className="lpin" aria-hidden="true">📍</span>
              <div className="lgrow">
                <div className="lk">Location</div>
                <div className="la">{v.address}</div>
              </div>
            </div>
          ) : null}

          {v.phone && (
            <a className="lrow link" href={`tel:${v.phone.replace(/\s+/g, '')}`}
              onClick={() => track('call_tapped', { restaurant_id: v.id, source_screen: 'venue' })}>
              <div className="lgrow">
                <div className="lk">Contact</div>
                <div className="lv">{v.phone}</div>
              </div>
            </a>
          )}
        </div>
      </div>

      {/* ── the slim card the other seven tabs get instead ──────────────── */}
      <div className="idcard">
        <div className="idc-top">
          <div className="idc-id">
            {/* `11_Restaurant.html #idcardThumb` — the venue's own photo,
                small, beside its name; drawn only when there is one */}
            {photos.length > 0 && <img className="idc-thumb" src={photos[0]} alt={v.name} />}
            <h1 className="restName">{nameMain}{nameSub && <span className="restNameZh">{nameSub}</span>}</h1>
          </div>
          {/* side by side at the right, one size, level with the name —
              walked live 12 Oct 2026: the heart floated mid-row, 10 px
              bigger and lower than Share ("the hearts are skew") */}
          <div className="idc-acts">
            {heart('')}
            {shareBtn('')}
          </div>
        </div>
        {aboutText(v).tagline && <div className="restTag">{aboutText(v).tagline}</div>}
        <div className="ratingRow">
          {v.rating_shown ? (
            <>
              <span className="stars">{stars}</span>
              {Number(v.rating).toFixed(1)}
              <span className="mut">· ({v.review_count})</span>
            </>
          ) : google ? (
            <GoogleStars g={google} />
          ) : (
            <span className="mut">{v.review_count === 0
              ? 'No reviews yet'
              : `${v.review_count} review${v.review_count === 1 ? '' : 's'}`}</span>
          )}
        </div>
        <div className="idtags">
          {bandName && <span className="tag">{bandName}</span>}
          {v.area && <span className="tag">{areaName}</span>}
          {cuisines[0] && <span className="tag">{label(cuisines[0])}</span>}
        </div>
      </div>

      {/* ── the tabs THIS venue has ──────────────────────────────────────── */}
      <div className="tabs" role="tablist" ref={tabsRef}>
        {shownTabs.map((t) => (
          <button
            key={t.key}
            className={t.key === activeTab ? 'tab active' : 'tab'}
            type="button"
            role="tab"
            aria-selected={t.key === activeTab}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="content">
        {activeTab === 'about' && (
          <div className="pane on">
            {aboutText(v).description && <p className="aboutText">{aboutText(v).description}</p>}
            {establishedLine(v) && <p className="estab" translate="no">{establishedLine(v)}</p>}
            <div className="vsub">FACILITIES &amp; FEATURES</div>
            {/* ⚠️ "NOT ANSWERED" AND "NONE OF THESE" ARE DIFFERENT ANSWERS, and
                migration 055 exists to keep them apart. An Eleater in a
                wheelchair needs to know which one they are looking at. */}
            {/* ⚠️ `facilities` COMES FROM THE SERVER ALREADY NAMED AND ALREADY
                FILTERED. It is taxonomy_terms rows where eleater_facing is
                true — the column's own comment says "some are data-only, e.g.
                air conditioning", and this list was showing those too because
                it read the venue's raw keys instead of the taxonomy. */}
            {v.venue_attributes_answered_at
              ? (facilities.length || ownFac.length
                  ? <div className="featwrap">
                      {facilities.map((f) => (
                        <div key={f.key} className="feat">
                          {/* ⚠️ Al, 12 September 2026: "you should have dots in
                              persimmon." The column was an empty span holding
                              18px of nothing, which is the gap he kept seeing. */}
                          <span className="fic" aria-hidden="true" />
                          {label(f)}
                        </div>
                      ))}
                      {ownFac.map((t, i) => (
                        <div key={'own' + i} className="feat"><span className="fic" aria-hidden="true" />{t}</div>
                      ))}
                    </div>
                  : <p className="mut">This place has told us none of these apply.</p>)
              : <p className="mut">This place has not answered yet.</p>}
            {byoLines(v).length > 0 && (
              <div className="byo" translate="no">
                {byoLines(v).map(([k, val]) => (
                  <div key={k} className="byorow"><span className="k">{k}</span><span className="v">{val}</span></div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 172 — the restaurant's Go live switch for its Signature Menu */}
        {activeTab === 'menu' && <List pane={v.signature_menu_live === false ? [] : pane.menu} empty={<><b>No menu yet</b> {`${v.name} has not published its Signature Menu on Eleat.`}</>}
          render={(rows) => <MenuPane rows={rows} cat={cat} setCat={setCat} venueId={id} />} />}

        {/* ⚠️ THE DEALS ARE ALREADY HERE, filtered out of the single call made
            on mount. `deals === null` is still-asking; an empty array after a
            venue has been read means this venue has none of that type — and in
            that case the tab is not on the strip, so this never renders the
            empty line for a deal type the venue does not do. */}
        {/* ⚠️⚠️ GAME DAY IS SERVED FROM ITS OWN CALL AND ITS OWN PANE.
            It is still on the tab strip as a deal tab — that is `tabsFor()`'s
            job and unchanged — but what it DRAWS is the game and the menu
            around it, from migration 019's tables. `List` is not used because
            `List` turns a pane into rows and throws everything else away,
            which is exactly how the review sub-scores went missing. */}
        {activeTab === 'gd' && (
          pane.gd === undefined ? <p className="mut">Loading…</p>
            : pane.gd && pane.gd.error ? <p className="err">{pane.gd.error}</p>
              : <GameDayPane data={pane.gd} tz={v.timezone} clockFormat={v.time_format} venueId={id} />
        )}

        {shownTabs.filter((t) => t.deal && t.key !== 'gd').map((t) => t.key === activeTab && (
          <List key={t.key}
            pane={deals === null ? undefined : deals.filter((d) => d.deal_type === t.deal)}
            empty={`No ${t.label} at the moment.`}
            render={(rows) => <DealPane rows={rows} kind={t.key} day={day} setDay={setDay} tz={v.timezone} venueId={id} />} />
        ))}

        {activeTab === 'feeds' && <List pane={pane.feeds} empty={<><b>No posts yet</b> {`${v.name} has not posted to its Feed.`}</>}
          /* ⚠️ `venue` AND `venueId` ARE NEW HERE AND ARE NEEDED: a Feed post's
             share message is led by the caption and ends on the restaurant's
             NAME, and the event is recorded against the restaurant's id. This
             pane had neither — it was drawing pictures and nothing else. */
          render={(rows) => <FeedsPane rows={rows} filt={ffilt} setFilt={setFfilt}
            venue={v} venueId={id} />} />}

        {activeTab === 'reviews' && <List pane={pane.reviews} empty={<><b>No reviews yet</b> {`Nobody has reviewed ${v.name} on Eleat. A review needs a verified visit.`}</>}
          render={(rows) => (
            /* ⚠️ THE SUMMARY IS HANDED IN SEPARATELY BECAUSE `List` THROWS IT
             * AWAY. /reviews/restaurant/<id> answers `{summary, reviews}`;
             * List exists to turn any pane into rows, so `summary.cards` —
             * the Food · Service · Ambiance · Value averages the locked
             * screen draws — never reached this component. Read off the live
             * response, not inferred. */
            <ReviewsPane rows={rows} venue={v}
              summary={(pane.reviews && pane.reviews.summary) || null} />
          )} />}
        {/* 226 · Google's reviews, BELOW Eleat's own and in their own box, while
            Eleat has fewer than five (Google's rule: kept apart and labelled).
            Outside List so it shows even when no Eleater has reviewed yet. */}
        {activeTab === 'reviews' && google && <GoogleReviews g={google} />}
      </div>

      {/* ⚠️ THE VENUE PAGE HAD NO TAB BAR AT ALL — 23 September 2026.
          Every locked screen ends with `nav.ptabbar`, including
          `11_Restaurant.html` and `12_Sports_Bar.html`. This screen rendered
          none, so an Eleater who tapped a card from Home reached the one page
          in the app with no way out except the back chevron.

          ⚠️ NO TAB IS MARKED ACTIVE, and that is deliberate rather than an
          omission: a venue is not one of the five destinations. It is reached
          FROM one of them, and lighting up Home while looking at a restaurant
          would say the Eleater is somewhere they are not. */}
      {saveErr && <p className="err" role="status" style={{ position: 'fixed', left: 16, right: 16, bottom: 96, zIndex: 40, textAlign: 'center' }}>{saveErr}</p>}
      <TabBar />
    </div>
  );
}

/* ⚠️ ONE PLACE DECIDES WHAT "STILL LOADING", "NOTHING HERE" AND "IT BROKE"
 * LOOK LIKE. Six tabs need all three states; six copies would drift and one of
 * them would end up showing an empty list where a failure happened. */
function List({ pane, empty, render }) {
  if (pane === undefined) return <div className="pane on"><p className="mut">Loading…</p></div>;
  if (pane && pane.error) return <div className="pane on"><p className="err">{pane.error}</p></div>;
  /* ⚠️ `reviews` IS IN THIS LIST BECAUSE THE SERVER SAYS SO, not because it
   * sounded likely. Menu, Feeds and Deals each answer with a bare array;
   * /reviews/restaurant/<id> answers with `{summary, reviews}` — it carries
   * the score card as well as the list. I guessed a chain of plausible names
   * (items · results · rows), none of them was `reviews`, and so this found
   * nothing and rendered "No reviews yet — be among the first."
   *
   * ⚠️ THAT IS THE WORST SHAPE A BUG CAN TAKE. It is not an error and it is
   * not a blank screen; it is a confident, well-written sentence that is
   * false. Al would have run the import, seen sixty venues still saying
   * "No reviews yet", and we would have spent the evening in the database —
   * which is where the reviews would have been, correctly, all along.
   *
   * Every key below was read off a real response from the live API on
   * 18 September, not inferred. */
  const rows = Array.isArray(pane)
    ? pane
    : (pane && (pane.reviews || pane.items || pane.results || pane.rows)) || [];
  if (!rows.length) return <div className="pane on"><p className="mut">{empty}</p></div>;
  return <div className="pane on">{render(rows)}</div>;
}

/* ⚠️ `pretty()` WAS HERE AND IT IS GONE — 18 September 2026.
 *
 * It took a database key, removed the underscores and capitalised the first
 * letter, and every label on this screen went through it. It was labelled a
 * stopgap in a comment that also said the real answer was taxonomy_terms and
 * cuisines. The stopgap shipped; the real answer did not, until Al asked
 * whether I had actually read the schema.
 *
 * ⚠️ IT IS DELETED RATHER THAN LEFT UNUSED, on purpose. A helper that turns a
 * key into a plausible label is a loaded gun in a file like this: the next
 * person needing a label in a hurry uses it, and the fault comes straight
 * back. There is now no way to render a label on this screen except the one
 * the database gives.
 */

/* ══════════════════════════════════════════════════════════════════════════
 * THE FOUR PANES, BUILT TO MATCH `Eleater Prototype/11_Restaurant.html`
 *
 * ⚠️ AL, 20 September 2026: *"you've got to connect everything that looks
 * exactly like the HTML that's in the schema that's locked in. Anything else
 * is a fail."*
 *
 * Every class name below is the locked screen's own — .mrow, .mthumb, .mcat,
 * .dow, .dowd, .ffilters, .fgrid, .scorecard. Nothing is renamed to something
 * tidier: a class renamed here is a rule in eleat.css that stops matching, and
 * that is precisely how a contrast fix once landed on .ftheart while the real
 * class was .fheart, and nobody could see why the screen had not changed.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️ THE WORDS ARE THE SCHEMA'S, NOT MINE. The section labels come from the
 * taxonomy via SECTIONS at the top of this file, and the order is the enum's
 * own — alcoholic · food · combos · non_alcoholic. */
function MenuPane({ rows, cat, setCat, venueId }) {
  /* ⚠️ WHICH PART OF A MENU PEOPLE ACTUALLY OPEN. The registry requires
     metadata.category, so the name of the section is the event — this is the
     number that tells a restaurant nobody ever taps "Desserts". */
  const pickCat = (c) => {
    setCat(c);
    if (c) track('category_click', {
      restaurant_id: venueId, source_screen: 'venue', metadata: { category: c },
    });
  };
  // The jump bar lists the categories the venue actually uses, in menu order.
  const cats = [];
  for (const r of rows) if (r.category && !cats.includes(r.category)) cats.push(r.category);
  const shown = cat && cats.includes(cat) ? rows.filter((r) => r.category === cat) : rows;

  return (
    <>
      {cats.length > 1 && (
        <div className="msub">
          <button className={'mcat' + (!cat ? ' active' : '')} onClick={() => pickCat(null)}>All</button>
          {cats.map((c) => (
            <button key={c} className={'mcat' + (cat === c ? ' active' : '')}
              onClick={() => pickCat(cat === c ? null : c)}>{c}</button>
          ))}
        </div>
      )}

      {SECTIONS.map(([key, label]) => {
        const items = shown.filter((r) => r.section === key);
        if (!items.length) return null;
        return (
          <div key={key} className="msec">
            <div className="msec-h">{label}</div>
            {items.map((m) => <MenuRow key={m.id} m={m} />)}
          </div>
        );
      })}
    </>
  );
}

/* One row, one shape, used by the menu AND by every deal — because on the
 * locked screen they ARE the same row. A deal item that rendered differently
 * from the menu item it references is two designs for one thing. */
function ItemSheet({ item, venue, onClose }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  return (
    <div className="sheetwrap show" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sheet itemsheet">
        <div className="sh-grab" />
        <button className="sh-x" type="button" aria-label="Close" onClick={onClose}>&times;</button>
        {item.image_url && <div className="sh-photo"><img src={item.image_url} alt="" /></div>}
        <div className="sh-name">{item.name}</div>
        {/* the restaurant's own name for the dish, under a translation — what
            to point at on the menu in the restaurant */}
        {item.original && <div className="sh-desc notranslate">{item.original}</div>}
        {item.description && <div className="sh-desc">{item.description}</div>}
        {item.offer && <div className="gtags"><span className="gtag">{item.offer}</span></div>}
        {priceText(item.price) && <div className="sh-price">{priceText(item.price)}</div>}
        {item.sold_out && <div className="sh-sold">Sold out</div>}
        <button className="sh-share" type="button"
          onClick={() => openShare({ ...shareVenue(venue), venueId: venue.id, subjectType: 'restaurant' })}>
          Share
        </button>
      </div>
    </div>
  );
}

/* ⚠️ A DISH OPENS ITS OWN SHEET — `11_Restaurant.html #itemSheet`: photo
 * (only if the restaurant added one), name, description, price, Share. One
 * event, so every menu — and Game Day in its own file — opens the same sheet. */
export const OPEN_ITEM = 'eleat:item';
function openItem(it) { window.dispatchEvent(new CustomEvent(OPEN_ITEM, { detail: it })); }

/* ⚠️ SOLD OUT — 114, Al 1 Oct 2026, from a WeChat menu he used: the dish
 * greyed out, the words "Sold out", and still tappable to read about it.
 * The server decides it (eleat_sold_out_now): today only on Happy Hour, Daily
 * and Game Day; for good on a Last-Minute Special. Two states, never a count
 * (068) — and "Sold out" is eleat_price.js's own locked word. */
function MenuRow({ m, venueId = null, dealId = null }) {
  const price = m.price != null ? m.price : m.base_price;
  /* the dish in the reader's language (Zaryna's translation, 013), else the
     restaurant's own words — 5 Oct 2026 */
  const d = dish(m);
  const sold = m.sold_out === true;
  /* a dish opened from an offer is an "offer open" — Analytics' second step */
  const opened = () => {
    /* the dish too (16 Oct 2026): "what dish do Eleaters open most?" had no
       answer — the event said which offer, never which dish */
    if (venueId && dealId) track('offer_opened', { restaurant_id: venueId, deal_id: dealId, source_screen: 'venue',
      metadata: { dish: m.name_override || m.name || null, menu_item_id: m.menu_item_id || null } });
  };
  return (
    <div className={sold ? 'mrow soldout' : 'mrow'} role="button" tabIndex={0}
      aria-disabled={sold || undefined}
      onClick={() => { opened(); openItem({ name: d.name, description: d.description, original: d.original,
        price, image_url: m.image_url, offer: m.discount_text, sold_out: sold }); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.click(); }}>
      <div className="l">
        <div className="mn">{d.name}</div>
        {d.description && <div className="md">{d.description}</div>}
        {/* the deal's own wording ("2-for-1", "$5 off") under the dish's
            description — Game Day's `deal_text` is drawn the same way */}
        {m.discount_text && (
          <div className="gtags"><span className="gtag">{m.discount_text}</span></div>
        )}
      </div>
      {(price != null || sold) && (
        <div className="mp">
          {price != null && priceText(price)}
          {sold && <div className="soldtag">Sold out</div>}
          {/* ⚠️ THERE IS NO `price_before` ON deal_offer_items — I wrote one and
            * checked afterwards. The columns are `price`, `bundle_price` and
            * `discount_text`. A struck-through original needs a column that
            * holds it, and that is a schema question, not something to fake
            * here by reaching for the menu item's price. */}
        </div>
      )}
      {m.image_url
        ? <div className="mthumb"><img src={m.image_url} alt="" loading="lazy"
            onError={(e) => { e.currentTarget.closest('.mthumb').classList.add('hold');
                              e.currentTarget.remove(); }} /></div>
        : <div className="mthumb hold" />}
    </div>
  );
}

/* ⚠️ DAYS is already defined at the top of this file — a second copy here
 * shadowed nothing and broke the module outright. Two copies of one list is
 * the root pattern again, caught this time by the parser rather than by Al. */

function DealPane({ rows, kind, day, setDay, tz, venueId }) {
  /* ⚠️ EVERY FIELD BELOW WAS CHECKED AGAINST THE SCHEMA, AND MY FIRST VERSION
   * OF THIS PANE HAD FOUR OF THEM WRONG. I wrote `starts_at`, `ends_at`,
   * `sponsor_name`, `dine_in_only` and `price_before` from memory of what a
   * deal *ought* to carry. The real columns are `start_time`, `end_time` and
   * `daily_repeat_days`; there is no sponsor and no dine-in flag on `deals` at
   * all. Every one of those would have rendered as nothing — a pane that looks
   * built and shows blank, which is the worst kind of finished.
   *
   * RULE ZERO: open the schema, every time. I did not, and the only reason it
   * did not reach Al is that I checked before saying it was done.
   *
   * ⚠️ DAYS COME FROM TWO PLACES AND THAT IS THE SCHEMA'S SHAPE, NOT A BUG:
   *   · deals.daily_repeat_days  int[], 0=Mon … 6=Sat  — Daily Deals
   *   · happy_hour_schedules     one row per day       — Happy Hour
   * Both use the SAME numbering, deliberately (migration 081's own comment:
   * "two numberings for one weekday is how two screens end up a day apart").
   * The Happy Hour rows are not joined into the public query yet, so those
   * deals arrive with no days and fall through to "runs every day" — which is
   * the safe direction: showing a live offer on a day it is not on is a
   * smaller harm than hiding one the venue is actually running. */
  /* ⚠️ A DAILY DEAL WITH CALENDAR DATES RUNS ON THOSE DATES — 3 Oct 2026.
     The strip is this week, Monday first, in the venue's own clock; a deal
     planned for the 14th is not on every Tuesday. */
  const weekDates = thisWeekDates(tz);
  const runsOn = (d, idx) => {
    if (Array.isArray(d.dates) && d.dates.length) return d.dates.includes(weekDates[idx]);
    const days = d.daily_repeat_days;
    return !Array.isArray(days) || !days.length || days.includes(idx);
  };

  // ⚠️ A DAY WITH NOTHING ON IT IS DIMMED, NOT REMOVED — the strip must not
  // change width as you tap along it.
  const has = DAYS.map((_, i) => rows.some((d) => runsOn(d, i)));
  const todayIdx = DAYS.findIndex(([k]) => k === todayKey(tz));
  const sel = day;
  const shown = sel == null ? rows : rows.filter((d) => runsOn(d, sel));

  return (
    <>
      {/* ⚠️ LAST-MINUTE HAS NO DAY ROW — the locked `#lm` pane has none: it is
          on now, until it ends or sells out. A Mon–Sun strip over it asked a
          question the offer cannot answer. */}
      {kind !== 'lm' && (
      <div className="dow">
        <button className={sel == null ? 'active' : ''} onClick={() => setDay(null)}>All week</button>
        {DAYS.map(([k, lbl], i) => (
          <button key={k}
            className={(sel === i ? 'active' : '') + (has[i] ? '' : ' quiet')}
            onClick={() => setDay(sel === i ? null : i)}>{lbl}</button>
        ))}
      </div>
      )}

      {!shown.length && <p className="mut">Nothing on {DAYS[sel][1]}.</p>}

      {(kind === 'lm' ? rows : shown).map((d) => (
        <div key={d.id} className="dealblock">
          <div className="dealhead">
            <div className="dh-t">{dealText(d).title}</div>
            {dealText(d).description && <div className="dh-s">{dealText(d).description}</div>}
          </div>

          {/* ⚠️ start_time / end_time — the columns' real names. */}
          {/* ⚠️ ONE LINE PER KIND — `dealWhen` above says why. */}
          {dealWhen(d, kind, sel, tz, runsOn(d, todayIdx)) && (
            <div className="dtime">{dealWhen(d, kind, sel, tz, runsOn(d, todayIdx))}</div>
          )}

          {/* ⚠️ "Dine-in only" IS ON THE LOCKED SCREEN, and it is read from
            * `allowed_fulfillment` — the column that actually decides it —
            * rather than from a `dine_in_only` flag, which does not exist.
            * It is not decoration: it is the difference between walking over
            * and finding the offer does not apply. */}
          {d.allowed_fulfillment === 'dine_in' && <div className="mut">Dine-in only</div>}

          {(d.items || []).map((i, k) => <MenuRow key={i.id || k} m={i} venueId={venueId} dealId={d.id} />)}
          {d.terms && <p className="terms">{d.terms}</p>}
        </div>
      ))}
    </>
  );
}

/* ⚠️ THE FIVE FILTERS ARE THE SCHEMA'S OWN, from feeds.category:
 * all/food/kitchen/bar/restaurant/staff. `staff` is deliberately NOT offered
 * to an Eleater — it is the restaurant's own internal category. */
const FFILTERS = [['all','All'],['food','Food'],['bar','Bar'],
                  ['kitchen','Kitchen'],['restaurant','Restaurant']];

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️⚠️ TAPPING A FEED POST OPENED NOTHING — BUILT 29 SEPTEMBER 2026.
 *
 * Al: *"you have not built anything to the approval of the locked HTMLs for
 * the Eleater side. Just look at it."*  Measured against
 * `11_Restaurant.html`, and this was the worst of what came back: the locked
 * screen carries a whole full-screen viewer — `.fviewer` · `.fvmedia` ·
 * `.fv-rail` · `.fv-foot` — and React drew a grid of photographs that did
 * nothing when touched. **A dead control, not a missing feature.**
 *
 * ⚠️ EVERY VALUE HERE IS THE LOCKED FILE'S OWN, selector for selector, and
 * `eleat_feeds.js` holds the rules the markup does not: the caption is led by
 * the author inline and never a separate byline row, it clamps to two lines
 * with "more", and the rail's three words exist because *"Like = this photo.
 * Save = the restaurant. They write to two different tables."*
 *
 * ⚠️⚠️ AND THE NAME IS READ, NEVER INVENTED — RULE ZERO-B. The locked sample
 * puts "Chef Leo" in that slot and **there is no Chef Leo**; what survives the
 * wipe is the SHAPE. `feeds.staff_name` and `feeds.staff_role` are real
 * columns a restaurant fills in, so a post that has them shows them, and a
 * post that does not falls back to the restaurant's own name. Nothing is
 * composed and no placeholder stands in for a person.
 *
 * ⚠️ THE COUNT IS REAL OR IT IS ABSENT. `like_count` now comes off
 * `feed_saves`; before today there was no number and drawing one would have
 * been a figure I made up under a control that looks authoritative.
 * ══════════════════════════════════════════════════════════════════════════ */
/* ⚠️ `eleat_feeds.js`'s OWN PATHS — 2 October 2026. HEART_D used to be
   'M12 21s-7-6.3-7-11a7 7…', which is a MAP PIN: the Like on every Feed post
   read as a location marker. The locked heart is the one every card in Eleat
   draws (`ui.jsx HEART_PATH`). */
const HEART_D = 'M12 20.5l-1.3-1.2C5.5 14.7 2.2 11.7 2.2 8 2.2 5.4 4.3 3.4 6.9 3.4c1.5 0 2.9.7 3.8 1.8L12 6.6l1.3-1.4c.9-1.1 2.3-1.8 3.8-1.8 2.6 0 4.7 2 4.7 4.6 0 3.7-3.3 6.7-8.5 11.3L12 20.5z';
const BOOKMARK_D = 'M5.5 3.5h13v17l-6.5-4.6-6.5 4.6v-17z';

/* `eleat_feeds.js`'s own count shape — a bare number on the rail, because the
   word sits underneath it as the label. */
function likeCount(n) {
  const v = Number(n) || 0;
  if (v >= 1000000) return (v / 1000000).toFixed(v % 1000000 === 0 ? 0 : 1) + 'M';
  if (v >= 1000) return (v / 1000).toFixed(v % 1000 === 0 ? 0 : 1) + 'k';
  return String(v);
}

function FeedViewer({ posts, at, setAt, venue, venueId }) {
  const savedSet = useSaved();
  const liked = useLiked();
  const [open, setOpen] = useState(false);

  /* ⚠️ THE CAPTION'S OWN STATE RESETS WITH THE POST. Without this, expanding
     one caption leaves the next post already expanded, which reads as a
     different post having no "more" to give. */
  useEffect(() => { setOpen(false); }, [at]);

  /* ⚠️ ESCAPE CLOSES IT. The locked screen is a phone frame and never needed
     one; a browser tab does, and a full-screen layer with no keyboard way out
     is a trap on a desktop. */
  useEffect(() => {
    if (at == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setAt(null);
      if (e.key === 'ArrowLeft' && at > 0) setAt(at - 1);
      if (e.key === 'ArrowRight' && at < posts.length - 1) setAt(at + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [at, posts.length, setAt]);

  /* ⚠️ EVERY HOOK ABOVE THE EARLY RETURN — 1 Oct 2026. These two sat below
     it, so opening a post (at: null → a number) called two MORE hooks than the
     draw before, and React stopped the whole restaurant page (error 310,
     "Eleat stopped here") the moment anybody tapped a Feed post. */
  const [delta, setDelta] = useState(0);
  useEffect(() => { setDelta(0); }, [at]);
  /* one post view each time a post is on screen — Analytics' Feeds page */
  const seenId = at != null && posts[at] ? posts[at].id : null;
  useEffect(() => {
    if (seenId && venueId) track('feed_viewed', { restaurant_id: venueId, source_screen: 'venue', metadata: { feed_id: seenId } });
  }, [seenId, venueId]);

  if (at == null || !posts[at]) return null;
  const f = posts[at];

  /* the restaurant is the poster; a named member of staff is shown when the
     restaurant recorded one */
  const who = (f.staff_name && f.staff_name.trim()) || (venue && venue.name) || '';
  const where = (f.staff_role && f.staff_role.trim())
    || (FFILTERS.find(([k]) => k === f.category) || [])[1] || '';
  const initials = who.trim().split(/\s+/).map((w) => w.charAt(0))
    .slice(0, 2).join('').toUpperCase();

  const on = liked.has(f.id);
  /* ══════════════════════════════════════════════════════════════════════
     ⚠️⚠️ THE COUNT MOVES WITH THE TAP, AND IT COUNTS THE TAP, NOT THE STATE.

     The locked script does move it — *"the like count moves with the tap, the
     way it does on IG"* — and my first version read `base + (on ? 1 : 0)`,
     which is wrong for the one Eleater who matters most: somebody who had
     ALREADY liked the post. **The server's `like_count` comes off `feed_saves`
     and already includes them**, so adding one for the filled heart counts the
     same person twice, and it would have looked perfectly plausible.

     ⚠️ NOTHING WOULD HAVE REPORTED IT. A number one too high renders exactly
     as well as a right one — the signature of every fault in this file.

     So this tracks MY OWN CHANGE since the count was read, not my state: zero
     on arrival, ±1 per tap, reset when the post changes. Two taps back to the
     start land on the server's number again. */
  const base = Number(f.like_count) || 0;
  /* ⚠️ FLOORED AT ZERO. An unlike that raced the server would otherwise print
     "-1", which is not a number of people. */
  const shownCount = Math.max(0, base + delta);

  function toggleLike() {
    /* ⚠️ SIGNED OUT GOES TO SIGN-IN. A tap that does nothing is the dead
       control this project keeps clearing out, and liking needs an account. */
    if (needsAccount('like', on ? null : { like: f.id })) return;
    const next = !on;
    markLiked(f.id, next);
    setDelta((d) => d + (next ? 1 : -1));
    api.likeFeed(f.id, next)
      /* ⚠️ A FAILED WRITE FLIPS BOTH BACK. A heart that filled and saved
         nothing is a silent lie — `saved.js`'s rule, applied here — and a
         count left one high is the same lie with a number on it. */
      .catch(() => {
        markLiked(f.id, !next);
        setDelta((d) => d - (next ? 1 : -1));
      });
  }

  const stamp = f.created_at
    ? new Date(f.created_at).toLocaleDateString('en-GB',
      { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  return (
    <div className="fviewer show" role="dialog" aria-modal="true"
      aria-label={captionOf(f) || 'Feed post'}>
      <div className="fvtop">
        {/* ⚠️ THE WORD STAYS — the locked file's own reason: a bare arrow
            borrows a familiarity Eleat does not have, and Al could not find
            the unlabelled one. */}
        <button type="button" className="fvclose" onClick={() => setAt(null)}
          aria-label="Back"><span className="fvcx">&lsaquo;</span> Back</button>
        <span className="fvtitle">{(venue && venue.name) || ''}</span>
      </div>

      <div className="fvmedia">
        {at > 0 && (
          <button type="button" className="fvnav prev" aria-label="Previous"
            onClick={() => setAt(at - 1)}>&lsaquo;</button>
        )}
        <div className="fvframe">
          {f.media_url && <img src={f.media_url} alt={captionOf(f) || ''} />}
        </div>
        {at < posts.length - 1 && (
          <button type="button" className="fvnav next" aria-label="Next"
            onClick={() => setAt(at + 1)}>&rsaquo;</button>
        )}
      </div>

      {/* the rail, down the right, over the picture */}
      <div className="fv-rail">
        <button type="button" className={on ? 'fv-act on' : 'fv-act'}
          data-what="Like" aria-label="Like this photo" aria-pressed={on}
          onClick={toggleLike}>
          <svg width="26" height="26" viewBox="0 0 24 24"
            fill={on ? 'currentColor' : 'none'} stroke="currentColor"
            strokeWidth="1.8" aria-hidden="true"><path d={HEART_D} /></svg>
        </button>
        <span className="fv-count">{likeCount(shownCount)}</span>

        <button type="button" className="fv-act" data-what="Share"
          aria-label="Share this post"
          onClick={() => openShare({
            ...shareFeedPost(f, venue && venue.name),
            venueId,
            subjectType: 'feed',
            subjectId: f.id,
          })}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round"
            aria-hidden="true">
            {SHARE_PATHS.map((d) => <path key={d} d={d} />)}
          </svg>
        </button>

        {/* ⚠️ SAVE IS THE RESTAURANT, NOT THE PHOTOGRAPH, and the word under
            it is what makes that legible — the locked file's own note. It is
            the same `markSaved` every card on every screen reads. */}
        {/* ⚠️ IT TOGGLES AND SHOWS ITS STATE — audit, 7 Oct 2026: it only
            ever saved, never unsaved, and never looked saved (the locked
            viewer toggles). The same shared list every heart reads. */}
        <button type="button" className={savedSet.has(venueId) ? 'fv-act on' : 'fv-act'} data-what="Save"
          aria-label="Save this restaurant" aria-pressed={savedSet.has(venueId)}
          onClick={() => {
            if (needsAccount('save', savedSet.has(venueId) ? null : { save: venueId })) return;
            const was = savedSet.has(venueId);
            markSaved(venueId, !was);
            (was ? api.unsave(venueId) : api.save(venueId)).catch(() => markSaved(venueId, was));
          }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"
            aria-hidden="true"><path d={BOOKMARK_D} /></svg>
        </button>
      </div>

      {/* the words, bottom left, over the picture */}
      <div className="fv-foot">
        <div className="fv-head">
          {initials && <span className="fv-av">{initials}</span>}
          <span className="fv-who">{who}</span>
          {where && <span className="fv-where">{where}</span>}
        </div>
        {captionOf(f) && (
          <div className={open ? 'fv-cap' : 'fv-cap clamped'}
            onClick={() => setOpen(true)}>
            <span className="t">{captionOf(f)}</span>
          </div>
        )}
        {/* ⚠️ "more" ONLY WHILE THERE IS MORE. A control that expands
            something already expanded is furniture. */}
        {captionOf(f) && !open && captionOf(f).length > 90 && (
          <button type="button" className="fv-more"
            onClick={() => setOpen(true)}>more</button>
        )}
        {stamp && <div className="fv-time">{stamp}</div>}
      </div>
    </div>
  );
}

function FeedsPane({ rows, filt, setFilt, venue, venueId }) {
  const shown = filt === 'all' ? rows : rows.filter((f) => f.category === filt);
  /* which post the viewer is showing — an index into `shown`, so Next and
     Previous walk the filter the Eleater is actually looking at */
  const [at, setAt] = useState(null);
  return (
    <>
      <div className="feedsTitle">Feeds</div>
      <div className="ffilters">
        {FFILTERS.map(([k, lbl]) => (
          <button key={k} className={'ffilter' + (filt === k ? ' active' : '')}
            onClick={() => setFilt(k)}>{lbl}</button>
        ))}
      </div>
      {!shown.length
        ? <p className="mut">Nothing under {FFILTERS.find(([k]) => k === filt)[1]} yet.</p>
        : <div className="fgrid">
            {/* ⚠️⚠️ THE CAPTION WAS FETCHED AND THROWN AWAY. `/feeds/restaurant/
                {id}` returns `caption` on every post and this rendered the
                photograph alone — so a restaurant wrote something about the
                dish and no Eleater ever saw it. The locked tile (`.ftile2` in
                `eleat_feeds.js`) carries `.fcap2` under the picture.
                ⚠️ AND THE CLASS WAS `feed`, which the locked file DELETED on
                29 August as never-rendering. `ftile` is the live one. */}
            {/* ⚠️⚠️ SHARE ON A FEED POST — `11_Restaurant.html #fvShare`, and it
                had never reached React either. Al, 12 September 2026: *"on feeds
                that is no fucking heart / like and share… how are we going to
                gather info on this."*
                ⚠️ THE CAPTION LEADS THE MESSAGE, not the restaurant's name.
                `eleat_feeds.js` says why: *"a share sent without the caption
                around it is a picture with no reason attached."*  The locked
                line is `(it.cap) + ' — ' + venueName + ' on Eleat'`, and it
                lives in `share.js` so it cannot be retyped differently here.
                ⚠️ AND IT IS RECORDED AS `subjectType: 'feed'` WITH THE POST'S
                OWN ID — migration 061 separates the four subjects for exactly
                this, and filing a post share as a restaurant share would make
                the restaurant's own Feed look like it earns nothing. */}
            {shown.map((f, i) => (
              <figure key={f.id} className="ftile">
                {/* ⚠️ THE PICTURE IS THE CONTROL, AND IT IS A REAL BUTTON.
                    An onClick on a `<figure>` is untappable by a keyboard and
                    invisible to a screen reader, and the locked grid's tile
                    opens the viewer — that is what the viewer is for. */}
                <button type="button" className="ftap"
                  aria-label={captionOf(f) ? `Open: ${captionOf(f)}` : 'Open this post'}
                  onClick={() => setAt(i)}>
                  {f.media_url && <img src={f.media_url} alt={captionOf(f) || ''} loading="lazy" />}
                </button>
                {captionOf(f) && <figcaption className="fcap">{captionOf(f)}</figcaption>}
                {/* Share for a post is the viewer's (#fvShare) — the one under each
                    tile sat inside the square photo and was never visible (Al,
                    7 Oct 2026: removed). */}
              </figure>
            ))}
          </div>}
      {/* ⚠️ IT IS MOUNTED WITH THE PANE, NOT WITH THE PAGE, so `at` indexes the
          same filtered list the grid is drawing. Mounted higher it would walk
          "All" while the Eleater is looking at "Bar". */}
      <FeedViewer posts={shown} at={at} setAt={setAt}
        venue={venue} venueId={venueId} />
    </>
  );
}

/* ⚠️ NO NUMBER UNTIL FIVE ELIGIBLE REVIEWS — and the gate is the venue's
 * `meets_threshold`, computed by the database trigger, never counted here.
 * Counting the rows this pane happens to have loaded would show a rating off
 * one page of reviews, which is a different number from the real one. */
const ASPECTS = [['rating_food', 'Food'], ['rating_service', 'Service'],
  ['rating_ambiance', 'Ambiance'], ['rating_value', 'Value']];
function initialsOf(name) {
  return String(name || '').split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0].toUpperCase()).join('') || 'E';
}
/* "today", "3 days ago", "2 weeks ago", "4 months ago" — the locked card's
   own kind of phrase, from the review's real date */
function whenAgo(iso) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const d = Math.floor((Date.now() - t) / 86400000);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 14) return `${d} days ago`;
  if (d < 60) return `${Math.round(d / 7)} weeks ago`;
  if (d < 365) return `${Math.round(d / 30)} months ago`;
  const y = Math.round(d / 365);
  return `${y} year${y === 1 ? '' : 's'} ago`;
}

/* ══════════════════════════════════════════════════════════════════════════
 * GOOGLE'S STARS AND REVIEWS — 226, Al-Noor 3 Nov 2026
 *
 * Drawn in Eleat's own design; Google sends only the facts. Google's rules
 * (Places policies): the words "Google Maps" stay visible beside its content,
 * it is kept apart from Eleat's own reviews, and every review credits its
 * writer — name, photo, a link to them — and links back to Google. Nothing here
 * is kept: it is asked for again each time the page opens.
 * ══════════════════════════════════════════════════════════════════════════ */
function GoogleStars({ g, label = true }) {
  return (
    <>
      <span className="stars"><Stars value={g.rating} /></span>
      {g.rating.toFixed(1)}
      <span className="mut">· ({g.count.toLocaleString()})</span>
      {label && <span className="gmaps">Google Maps</span>}
    </>
  );
}

function GoogleReviews({ g }) {
  return (
    <section className="greviews">
      <div className="ghead">
        <div>
          {/* the heading IS the attribution — "Google Maps", never translated */}
          <div className="gtitle"><span className="gmaps">Google Maps</span></div>
          <div className="ratingRow"><GoogleStars g={g} label={false} /></div>
        </div>
      </div>
      {g.reviews.map((r, i) => (
        <div className="rcard grev" key={i}>
          <div className="rhead">
            {r.photo
              ? <img className="ravatar gphoto" src={r.photo} alt="" referrerPolicy="no-referrer" loading="lazy" />
              : <div className="ravatar">{initialsOf(r.author)}</div>}
            <div className="rwho">
              <div className="rnm">{r.author_url
                ? <a href={r.author_url} target="_blank" rel="noopener noreferrer">{r.author}</a>
                : r.author}</div>
              {r.when && <div className="rmeta">{r.when}</div>}
            </div>
            {r.rating != null && <Stars value={r.rating} />}
          </div>
          {r.text && <div className="rbody">{r.text}</div>}
          {r.url && <a className="glink" href={r.url} target="_blank" rel="noopener noreferrer">View on Google Maps ›</a>}
        </div>
      ))}
      {g.maps_url && (
        <a className="gall" href={g.maps_url} target="_blank" rel="noopener noreferrer">
          {`See all ${g.count.toLocaleString()} reviews on Google Maps ›`}
        </a>
      )}
      <p className="mut gnote">Google decides which reviews it shares. Google does not verify reviews.</p>
    </section>
  );
}

function ReviewsPane({ rows, venue, summary }) {
  /* ══════════════════════════════════════════════════════════════════════
     ⚠️⚠️ THIS READ THREE FIELD NAMES THAT DO NOT EXIST, AND SO EVERY VENUE
     WITH A REAL RATING TOLD THE ELEATER IT HAD NONE.

     It asked for `meets_threshold`, `rating_avg` and `rating_count`.
     `GET /restaurants/{id}` returns `rating_shown`, `rating` and
     `review_count`. Three misses, so `avg` was always null and the pane
     printed "Not enough reviews for a rating yet" — on AMMO, which comes back
     `rating "4.80", rating_shown true, review_count 5`.

     ⚠️ A CONFIDENT FALSE SENTENCE IS WORSE THAN A BLANK. The screen did not
     look broken; it looked like a quiet new restaurant. Nothing threw and
     nothing logged, which is why it survived.

     ⚠️ `rating_avg` AND `meets_threshold` ARE REAL — they are the COLUMN
     names inside the search query, and they never leave it. Reading a column
     name from the wrong side of an API is the same class of mistake as the
     feed that asked for `/feed` instead of `/feeds`. */
  const shown = venue && (venue.rating_shown ?? venue.meets_threshold);
  const raw = venue && (venue.rating ?? venue.rating_avg);
  const avg = shown && raw != null ? Number(raw) : null;
  const totalRaw = venue && (venue.review_count ?? venue.rating_count);
  const total = totalRaw != null ? totalRaw : rows.length;

  // The distribution is over what was loaded, and says so — it is a shape, not a score.
  const dist = [5, 4, 3, 2, 1].map((n) => ({
    n, c: rows.filter((r) => Math.round(Number(r.rating)) === n).length,
  }));

  /* ── THE SCORE-CARD AVERAGES — `11_Restaurant.html .aspavg / .aspchip` ─────
   * The four labels and their order are the locked screen's own:
   *   Food · Service · Ambiance · Value
   * ⚠️ AMBIANCE, NOT ATMOSPHERE. The chip says Ambiance, the write sheet's row
   * says Ambiance, the column is `rating_ambiance` and the summary key is
   * `ambiance`. The ONLY place the word "atmosphere" appears is a shared-state
   * key inside the locked screen's own script, which says on the line that the
   * label stays Ambiance. Renaming it here would be RULE 4.
   *
   * ⚠️ A NULL IS NOT A ZERO, AND IT IS NOT A DASH EITHER. `computeRatingSummary`
   * in review.rules.js returns null for an aspect no review scored, and this
   * screen already says the Eleat thing about an absent rating one box up:
   * below the threshold there is no number to show, so none is shown. A chip
   * with no number behind it is dropped; if all four are null the row does not
   * render at all. `Food 0.0` would be a made-up bad score. */
  const CARDS = [['food', 'Food'], ['service', 'Service'],
    ['ambiance', 'Ambiance'], ['value', 'Value']];
  const cards = summary && summary.cards ? summary.cards : {};
  const chips = CARDS.filter(([k]) => cards[k] != null);

  /* ── THE SORT BAR — `.rsort / .rsortbtn`, the locked screen's four ────────
   * Most Recent · Highest · Lowest · With Response.
   * ⚠️ SORTED HERE, NOT RE-FETCHED. `GET /reviews/restaurant/:id` does take a
   * `sort` of highest/lowest, but it has no "with response" and a round trip
   * per tap would make a sort bar feel like a page load. The list is capped at
   * 200 rows by the service, so the whole of it is already in hand. */
  const [sort, setSort] = useState('recent');
  const list = (() => {
    const l = rows.slice();
    if (sort === 'highest') return l.sort((a, b) => Number(b.rating) - Number(a.rating));
    if (sort === 'lowest') return l.sort((a, b) => Number(a.rating) - Number(b.rating));
    if (sort === 'response') return l.filter((r) => r.response_body);
    return l.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  })();

  return (
    <>
      <WriteReview venue={venue} />

      <div className="scorecard">
        {avg != null ? (
          /* `11_Restaurant.html .sc-head`: the number out of five, the stars,
             and the count — "verified", because only a confirmed visit can
             leave one (the verified-visit gate). */
          <div className="sc-head">
            <div className="bignum">{avg.toFixed(1)}<span>/5</span></div>
            <div>
              <Stars value={avg} />
              <div className="mut">{total} review{total === 1 ? '' : 's'}</div>
            </div>
          </div>
        ) : (
          /* ⚠️ THE COPY SAYS WHY THERE IS NO NUMBER, rather than showing a zero
           * or an empty box. A venue with four good reviews is not a venue with
           * no rating — it is a venue that has not reached the threshold. */
          <div className="mut">
            Not enough reviews for a rating yet — Eleat shows one once five
            Eleaters who visited have written.
          </div>
        )}
        {/* `11_Restaurant.html .rdist`: 5★ → 1★, a bar, and the SHARE of
            reviews as a percentage — the locked screen's own shape. */}
        {rows.length > 0 && (
          <div className="rdist">
            {dist.map((d) => {
              const pct = Math.round((d.c / rows.length) * 100);
              return (
                <div key={d.n} className="distrow">
                  <span className="sl">{d.n}★</span>
                  <div className="track"><div className="fill" style={{ width: `${pct}%` }} /></div>
                  <b>{pct}%</b>
                </div>
              );
            })}
          </div>
        )}

        {chips.length > 0 && (
          <div className="aspavg">
            {chips.map(([k, label]) => (
              <span key={k} className="aspchip">{label} <b>{Number(cards[k]).toFixed(1)}</b></span>
            ))}
          </div>
        )}
      </div>

      <div className="rsort">
        {[['recent', 'Most Recent'], ['highest', 'Highest'],
          ['lowest', 'Lowest'], ['response', 'With Response']].map(([k, label]) => (
            <button key={k} type="button"
              className={sort === k ? 'rsortbtn active' : 'rsortbtn'}
              onClick={() => setSort(k)}>{label}</button>
          ))}
      </div>

      {/* ⚠️ THE EMPTY SENTENCE IS THE LOCKED SCREEN'S OWN, because it is the
          one filter that can empty a list that is not empty. A venue with
          five reviews and no replies would otherwise show nothing at all and
          read as a broken screen. */}
      {list.length === 0 ? (
        <p className="mut">No reviews with a response yet.</p>
      ) : list.map((r) => (
        /* ⚠️ THE LOCKED REVIEW CARD — `11_Restaurant.html renderReviews`:
           initials, the shown name, ✓ VERIFIED, "Eleater <id> · <when>", the
           stars, the words, the aspects the Eleater rated, then the reply.
           VERIFIED is true of every row by construction: the server refuses a
           review with no confirmed visit (reviews.service.js eligibleVisit).
           The aspects are the sub-scores they gave — Food, Service, Ambiance,
           Value — never invented. Dish ratings are Phase 2 and are not drawn. */
        <div key={r.id} className="rcard">
          <div className="rhead">
            <div className="ravatar">{initialsOf(r.reviewer_name)}</div>
            <div className="rwho">
              <div className="rnm">{r.reviewer_name}{/* [227] ✓ VERIFIED = the restaurant confirmed the visit; a review
                from "Yes, I reserved" + "Yes, I went" publishes without it */}
                {r.verified !== false && <> <span className="vbadge">✓ VERIFIED</span></>}</div>
              <div className="rmeta">
                {[r.reviewer_customer_no ? `Eleater ${r.reviewer_customer_no}` : null, whenAgo(r.created_at)]
                  .filter(Boolean).join(' · ')}
              </div>
            </div>
            {r.rating != null && <Stars value={Number(r.rating)} />}
          </div>
          {r.body && <div className="rbody">{r.body}</div>}
          {ASPECTS.some(([k]) => r[k] != null) && (
            <div className="asptags">
              {ASPECTS.filter(([k]) => r[k] != null).map(([k, label]) => (
                <span key={k} className="asptag">{label}</span>
              ))}
            </div>
          )}

          {/* ⚠️ THE RESTAURANT'S REPLY SITS UNDER THE REVIEW IT ANSWERS —
              `.rreply`, with `.rl` naming who replied and a `<p>` carrying the
              words. `response_body` has been on every row this screen reads
              since the route was written and nothing drew it, so an Eleater
              whose review had been answered could not see the answer.
              The locked screen's label is "↩ <venue> replied". */}
          {r.response_body && (
            <div className="rreply">
              <div className="rl">↩ {venue && venue.name} replied</div>
              <p>{r.response_body}</p>
            </div>
          )}
        </div>
      ))}

      {/* ⚠️ EXACTLY AS `11_Restaurant.html .vnote` WORDS IT, including the
          empty first span the locked markup carries. */}
      <div className="vnote">
        <span></span>
        <span>
          <b>Real reviews.</b> Every review comes from a confirmed visit, under
          a real name. Every one is from a signed-up Eleater, under their real name.
        </span>
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
 * WRITE A REVIEW — `.writebtn` + `.writehint`
 *
 * ⚠️ IT MUST NOT BE A DEAD CONTROL, and on this screen it very nearly was.
 * `POST /reviews/restaurant/:id` is real, but underneath it a review requires
 * `visits.confidence = 'confirmed'` — written by nothing except
 * `eleat_confirm_attendance()`, which a restaurant calls from Restaurant Admin
 * when the Eleater actually dined. Restaurant Admin does not exist yet, so
 * TODAY nobody is eligible anywhere in Eleat. That is the review gate working,
 * not a fault, and `Reviews.jsx` says the same thing about the empty "Your
 * reviews" screen.
 *
 * So the button asks the server the question instead of guessing:
 *   signed out          → the sign-in door, the same one Reviews.jsx offers
 *   GET /visits/can-review/<id> → can_review, and a REASONED no with its own
 *                         message — and for `no_visit` that message IS the
 *                         locked hint, "Reviews come from a confirmed visit."
 *
 * ⚠️ NOT ONE WORD BELOW IS MINE. The button, the hint and the sheet's labels
 * are `11_Restaurant.html`; every refusal sentence is the server's own
 * `message`, rendered as it arrives (visits.service.js reviewEligibility).
 * ══════════════════════════════════════════════════════════════════════════ */
function WriteReview({ venue }) {
  const id = venue && venue.id;
  const [note, setNote] = useState(null);    // the server's reason, shown as it came
  const [open, setOpen] = useState(false);   // the locked write sheet
  const [asking, setAsking] = useState(false);
  const [dinedAsk, setDinedAsk] = useState(null);   // [227] "Did you make it to …?"

  function ask() {
    if (!id || asking) return;
    if (needsAccount('review', { review: id })) return;
    setAsking(true);
    setNote(null);
    api.canReview(id)
      .then((d) => {
        if (d && d.can_review) setOpen(true);
        /* ⚠️ 227 (Al-Noor, 3 Nov 2026): booked through Eleat and the booking
           time has passed — ask whether they went, and Yes opens the sheet */
        else if (d && d.reason === 'ask_dined' && d.reservation_id) setDinedAsk(d.reservation_id);
        else setNote((d && d.message) || 'Reviews come from a confirmed visit.');
      })
      .catch((e) => setNote(e.message))
      .finally(() => setAsking(false));
  }

  function answerDined(answer) {
    const rid = dinedAsk;
    setDinedAsk(null);
    api.dinedAnswer(rid, answer)
      .then((d) => { if (answer === 'yes' && d && d.can_review) setOpen(true); })
      .catch((e) => setNote(e.message));
  }

  /* signed in from the gate to write this review — open it for them; and
     `&write=1` (the "Did you make it?" Yes, the email's button) does the same */
  useEffect(() => {
    if (!id || !getToken()) return;
    const write = new URLSearchParams(window.location.search).get('write') === '1';
    if (takePendingReview(id) || write) ask();
  }, [id]);

  return (
    <>
      <button className="writebtn" type="button" onClick={ask} disabled={asking}>
        Write a review
      </button>
      <div className="writehint">Reviews come from a confirmed visit.</div>
      {note && <p className="mut" aria-live="polite">{note}</p>}
      {dinedAsk && <DinedSheet venue={venue.name} answer={answerDined} close={() => setDinedAsk(null)} />}
      {open && <WriteSheet venue={venue} close={() => setOpen(false)} />}
    </>
  );
}

/* The locked write sheet — `11_Restaurant.html #reviewSheet`, its markup, its
 * labels and its order. The overall star is required; the four detail rows,
 * the recommend answer and the words are not, which is what
 * `reviewInput` in reviews.routes.js accepts.
 *
 * ⚠️ A SKIP IS NEVER A NO on the recommend row — migration 035, and the locked
 * screen's own comment. Tapping the chosen one again clears it. */
function WriteSheet({ venue, close }) {
  const [overall, setOverall] = useState(0);
  const [cats, setCats] = useState({ food: 0, service: 0, ambiance: 0, value: 0 });
  const [rec, setRec] = useState(null);
  const [text, setText] = useState('');
  const [msg, setMsg] = useState(null);
  const [sending, setSending] = useState(false);

  const ROWS = [['food', 'Food'], ['service', 'Service'],
    ['ambiance', 'Ambiance'], ['value', 'Value']];

  function submit() {
    if (sending) return;
    if (!overall) { setMsg('Tap a star rating first.'); return; }
    setSending(true);
    setMsg(null);
    const body = { rating: overall };
    ROWS.forEach(([k]) => { if (cats[k]) body['rating_' + k] = cats[k]; });
    if (rec !== null) body.would_recommend = rec;
    if (text.trim()) body.body = text.trim();
    api.writeReview(venue.id, body)
      .then(() => { close(); window.location.reload(); })
      .catch((e) => { setMsg(e.message); setSending(false); });
  }

  return (
    <div className="sheetwrap show" onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="sheet">
        <div className="sh-grab"></div>
        <div className="sh-name">Write a review</div>

        {/* the locked sheet centres this one label inline, and only this one */}
        <div className="wlabel" style={{ textAlign: 'center' }}>OVERALL — TAP A STAR</div>
        <div className="wstars">
          {[1, 2, 3, 4, 5].map((n) => (
            <span key={n} className={n <= overall ? 'wstar on' : 'wstar'}
              role="button" tabIndex={0}
              onClick={() => setOverall(n)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOverall(n); }}>★</span>
          ))}
        </div>

        <div className="wlabel">RATE THE DETAILS</div>
        <div className="wrows">
          {ROWS.map(([k, label]) => (
            <div key={k} className="wrow">
              <span className="wl">{label}</span>
              <span className="ws">
                {[1, 2, 3, 4, 5].map((n) => (
                  <span key={n} className={n <= cats[k] ? 'on' : undefined}
                    role="button" tabIndex={0}
                    onClick={() => setCats((c) => ({ ...c, [k]: n }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setCats((c) => ({ ...c, [k]: n }));
                    }}>★</span>
                ))}
              </span>
            </div>
          ))}
        </div>

        <div className="wlabel">WOULD YOU RECOMMEND US TO A FRIEND?</div>
        <div className="waspects">
          {[[true, 'Yes'], [false, 'No']].map(([v, label]) => (
            <button key={label} type="button"
              className={rec === v ? 'wasp on' : 'wasp'}
              onClick={() => setRec((cur) => (cur === v ? null : v))}>{label}</button>
          ))}
        </div>

        <textarea className="sh-notes" value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Any additional feedback?" />

        {msg && <div className="rvtoast" aria-live="polite">{msg}</div>}

        <div className="wbtns">
          <button className="wcancel" type="button" onClick={close}>Cancel</button>
          <button className="wsubmit" type="button" onClick={submit} disabled={sending}>Submit</button>
        </div>
      </div>
    </div>
  );
}

/* ⚠️ `Stars` MOVED TO `ui.jsx` ON 27 SEP 2026 — it was the only correct one of
 * three and it lived here, private, while two wrong ones drew the rating on
 * other screens. One owner; see the header on it there. */
